import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation, useBlocker } from "react-router-dom";
import {
  useActivityActions,
  useActivityFeedData,
  useActivityRecommendationsData,
} from "../contexts/ActivityContext";
import { useAuth } from "../contexts/AuthContext";
import {
  AIMessage,
  ParsedAIResponse,
  ActivityCategory,
  SearchAIResponse,
  ActivityIdea,
  User,
} from "../types";
import {
  generateActivitySuggestionStreamAuto,
} from "../services/geminiService";
import {
  generateActivitySuggestion as generateActivitySuggestionCallable,
  generateActivitySearchIdeas as generateActivitySearchIdeasCallable,
} from "../services/functionsApi";
import {
  getPlaceDetails,
  geocodeByAddress,
  loadGoogleMapsApi,
  getPlaceAutocomplete,
} from "../services/locationService";
import {
  ArrowLeftIcon,
  SendIcon,
  Edit2Icon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  AIIcon,
  CheckIcon,
  XIcon,
  ImageIcon,
  CameraIcon,
  RefreshCwIcon,
  BrainCircuitIcon,
  LinkIcon,
  SearchIcon,
} from "../components/icons/FeatherIcons";
import { motion, AnimatePresence } from "framer-motion";
import LocationAutocomplete from "../components/LocationAutocomplete";
import ActivityImageSelectionModal from "../components/ActivityImageSelectionModal";
import { useToast, ToastType } from "../contexts/ToastContext";
import { CATEGORIES, getValidCategory } from "../constants";
import {
  detectActivityCategory,
  getCategoryConfidence,
} from "../utils/categoryDetection";
import { getActivityImageUrl } from "../services/imageService";
import { toJsDate } from "../utils/toJsDate";
import { Capacitor } from "@capacitor/core";
import { KeyboardHandler } from "../utils/keyboardHandler";
import { createPortal } from "react-dom";
import { usePageTracking } from "../hooks/usePageTracking";
import { doc, updateDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";
// @ts-ignore
import fallBackImage from "../src/assets/1024.png";

import { crashlytics } from "../services/firebase";
// Helper function to convert groupSize to number, taking upper bound for ranges
const getGroupSizeAsNumber = (
  groupSize: number | string | undefined,
): number => {
  if (typeof groupSize === "number") return groupSize > 0 ? groupSize : 2;
  if (typeof groupSize === "string") {
    // Extract all numbers from strings like "3-4 people", "5+ people", "2 people"
    const numbers = groupSize.match(/\d+/g);
    if (numbers && numbers.length > 0) {
      // Take the highest number for ranges (e.g., "3-5 people" becomes 5)
      const maxNumber = Math.max(...numbers.map((n) => parseInt(n)));
      return maxNumber > 0 ? maxNumber : 2;
    }
  }
  return 2; // default
};

// Helper function to convert duration strings to minutes
const getDurationAsMinutes = (
  duration: number | string | undefined,
): number => {
  if (typeof duration === "number") return duration > 0 ? duration : 60;
  if (typeof duration === "string") {
    const lowerDuration = duration.toLowerCase();

    // Handle common duration formats
    if (lowerDuration.includes("hour")) {
      const hourMatch = lowerDuration.match(/(\d+(?:\.\d+)?)\s*hour/);
      if (hourMatch) {
        return Math.round(parseFloat(hourMatch[1]) * 60); // Convert hours to minutes
      }
    }

    if (lowerDuration.includes("minute") || lowerDuration.includes("min")) {
      const minuteMatch = lowerDuration.match(/(\d+)\s*(?:minute|min)/);
      if (minuteMatch) {
        return parseInt(minuteMatch[1]);
      }
    }

    if (lowerDuration.includes("half day")) {
      return 240; // 4 hours
    }

    if (lowerDuration.includes("full day") || lowerDuration.includes("day")) {
      return 480; // 8 hours
    }

    // Try to extract just a number (assume hours if no unit specified)
    const numberMatch = lowerDuration.match(/(\d+(?:\.\d+)?)/);
    if (numberMatch) {
      const num = parseFloat(numberMatch[1]);
      // If the number is small (1-12), assume hours; if large (30+), assume minutes
      return num <= 12 ? Math.round(num * 60) : num;
    }
  }
  return 60; // default 1 hour
};

// Utility to generate unique timestamps for React keys
let timestampCounter = 0;
const sessionId = Math.random().toString(36).substr(2, 9);
const generateUniqueTimestamp = (): string => {
  const now = new Date();
  timestampCounter += 1;
  return `${sessionId}-${now.toISOString()}-${timestampCounter}`;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

const UserBubble: React.FC<{ message: string }> = ({ message }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 10, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    className="flex justify-end"
  >
    <div className="bg-user-bubble text-white rounded-t-2xl rounded-bl-2xl py-2 px-4 max-w-xs md:max-w-md shadow-sm">
      <p className="text-base break-words whitespace-pre-wrap">{message}</p>
    </div>
  </motion.div>
);

interface PortalProps {
  children: React.ReactNode;
}

const Portal: React.FC<PortalProps> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

const ActivityReviewCard: React.FC<{
  data: ParsedAIResponse;
  onUpdate: (newData: ParsedAIResponse) => void;
  isApiLoaded: boolean;
  addToast: (message: string, type: ToastType) => void;
  onPost?: (data: ParsedAIResponse) => void;
  addToGlobalManualEdits: (fieldName: string) => void;
  onEditingInline?: (editingInline: boolean) => void;
  disabled?: boolean;
  onCreateFromProposal?: () => Promise<void>;
  isCreatingFromProposal?: boolean;
  proposalInviteeLabel?: string;
  proposalInviteeUsers?: User[];
  showManageInviteesModal?: boolean;
  setShowManageInviteesModal?: (value: boolean) => void;
  inviteeSearchQuery?: string;
  setInviteeSearchQuery?: (value: string) => void;
  inviteeSearchResults?: User[];
  setInviteeSearchResults?: (value: User[]) => void;
  inviteeSearchIsLoading?: boolean;
  inviteeSearchIsLoadingMore?: boolean;
  inviteeSearchHasMore?: boolean;
  loadMoreInviteeSearch?: () => void;
  handleRemoveInvitee?: (userId: string) => void;
  handleAddInvitee?: (userId: string) => void;
  handleSearchInvitees?: (query: string) => void;
}> = ({
  data,
  onUpdate,
  isApiLoaded,
  addToast,
  onPost,
  addToGlobalManualEdits,
  onEditingInline,
  disabled,
  onCreateFromProposal,
  isCreatingFromProposal,
  proposalInviteeLabel,
  proposalInviteeUsers = [],
  showManageInviteesModal = false,
  setShowManageInviteesModal,
  inviteeSearchQuery = "",
  setInviteeSearchQuery,
  inviteeSearchResults = [],
  setInviteeSearchResults,
  inviteeSearchIsLoading = false,
  inviteeSearchIsLoadingMore = false,
  inviteeSearchHasMore = false,
  loadMoreInviteeSearch,
  handleRemoveInvitee,
  handleAddInvitee,
  handleSearchInvitees,
}) => {
  const isAndroid = Capacitor.getPlatform() === "android";
  const isProposal = !!onCreateFromProposal;
  const hasInvitees = proposalInviteeUsers.length > 0;
  const inviteeLoadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const hasInviteeSearchQuery = inviteeSearchQuery.trim().length > 0;

  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingDateTime, setIsEditingDateTime] = useState(false);
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [isEditingGroupSize, setIsEditingGroupSize] = useState(false);
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(data.imageUrl || "");

  // Track the "identity" of the activity (category + title) to prevent refetching on detail changes
  const [imageFetchedFor, setImageFetchedFor] = useState<string>("");

  const [tempTitle, setTempTitle] = useState(data.title || "");
  const [tempDescription, setTempDescription] = useState(
    data.description || "",
  );
  const [tempDateTime, setTempDateTime] = useState(
    data.suggestedDateTime ||
      toJsDate(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  );
  const [tempDuration, setTempDuration] = useState(
    data.duration && getDurationAsMinutes(data.duration) > 0
      ? getDurationAsMinutes(data.duration)
      : 60,
  ); // Default 60 minutes (1 hour)
  // Helper function to check if groupSize is valid
  const isValidGroupSize = (groupSize: number | undefined): boolean => {
    return typeof groupSize === "number" && groupSize > 0;
  };

  const [tempGroupSize, setTempGroupSize] = useState(
    isValidGroupSize(data.groupSize) ? data.groupSize! : 2,
  ); // Default 2 people
  const [tempDurationInput, setTempDurationInput] = useState(
    String(
      data.duration && getDurationAsMinutes(data.duration) > 0
        ? getDurationAsMinutes(data.duration)
        : 60,
    ),
  ); // String for input
  const [tempGroupSizeInput, setTempGroupSizeInput] = useState(
    String(isValidGroupSize(data.groupSize) ? data.groupSize! : 2),
  ); // String for input
  const [tempCategory, setTempCategory] = useState(
    getValidCategory(data.category),
  );

  useEffect(() => {
    isEditingTitle ||
    isEditingCategory ||
    isEditingDescription ||
    isEditingLocation ||
    isEditingDateTime ||
    isEditingDuration ||
    isEditingGroupSize
      ? onEditingInline && onEditingInline(true)
      : onEditingInline && onEditingInline(false);
  }, [
    isEditingLocation,
    isEditingTitle,
    isEditingDescription,
    isEditingDateTime,
    isEditingDuration,
    isEditingGroupSize,
    isEditingCategory,
    onEditingInline,
  ]);

  useEffect(() => {
    if (!showManageInviteesModal || !loadMoreInviteeSearch) return;
    const trigger = inviteeLoadMoreTriggerRef.current;
    if (!trigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          inviteeSearchHasMore &&
          !inviteeSearchIsLoadingMore &&
          !inviteeSearchIsLoading
        ) {
          loadMoreInviteeSearch();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [
    showManageInviteesModal,
    loadMoreInviteeSearch,
    inviteeSearchHasMore,
    inviteeSearchIsLoadingMore,
    inviteeSearchIsLoading,
  ]);

  // Sync tempCategory with data changes
  useEffect(() => {
    setTempCategory(getValidCategory(data.category));
  }, [data.category]);

  // Initialize the actual data with proper defaults when missing
  const displayData = {
    ...data,
    duration:
      data.duration && getDurationAsMinutes(data.duration) > 0
        ? getDurationAsMinutes(data.duration)
        : 60,
    groupSize: isValidGroupSize(data.groupSize) ? data.groupSize! : 2,
    category: getValidCategory(data.category), // Ensure valid category
    suggestedDateTime:
      data.suggestedDateTime ||
      toJsDate(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // Default to tomorrow
    imageUrl: selectedImageUrl,
  };

  // Auto-fetch image ONCE when core activity identity (category + title) is established
  // Don't refetch when user adds ancillary details like location, time, group size
  useEffect(() => {
    // Create a stable identity key based on category and core title (first 3 words)
    // This prevents refetch when AI progressively refines the title (e.g., "Movie Night" → "Movie Night at Marina Bay")
    const coreTitle = displayData.title?.split(" ").slice(0, 3).join(" ") || "";
    const activityIdentity =
      `${displayData.category}-${coreTitle}`.toLowerCase();

    // Only fetch if:
    // 1. We have both category and title (core activity defined)
    // 2. We haven't already fetched an image for this identity
    // 3. User hasn't manually selected an image
    if (
      displayData.category &&
      displayData.title &&
      activityIdentity !== imageFetchedFor &&
      !selectedImageUrl
    ) {

      getActivityImageUrl(
        displayData.category as ActivityCategory,
        displayData.title, // Use FULL title for image search (not just core)
        displayData.description || "", // Use activity description (not user prompt!)
        displayData.location, // Include location context
      )
        .then((url) => {
          setSelectedImageUrl(url);
          setImageFetchedFor(activityIdentity); // Mark this identity as fetched
          onUpdate({ ...displayData, imageUrl: url });
        })
        .catch((error) => {
          crashlytics.logError(
            `Error fetching activity image: ${getErrorMessage(error)}`,
            "CreatePage.tsx",
            error,
          );
          // Failed to get default image, continue with fallback
        });
    } else if (activityIdentity === imageFetchedFor) {
    }
  }, [
    displayData.category,
    displayData.title,
    selectedImageUrl,
    imageFetchedFor,
  ]);

  useEffect(() => {
    if (showImageModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showImageModal]);

  const handleImageSelect = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    onUpdate({ ...displayData, imageUrl: imageUrl });
    setShowImageModal(false);
  };

  // Get comprehensive field status for smart validation
  const getFieldStatus = () => {
    // Check title (either saved or being edited)
    const hasTitle = isEditingTitle
      ? tempTitle.trim()
      : displayData.title?.trim();

    // Check location (always from displayData since it requires place selection)
    // Location must have both address and coordinates to be valid
    const hasLocation = displayData.location?.trim() && displayData.coordinates;

    // Check date/time (either saved or being edited)
    let hasValidDateTime = false;
    if (isEditingDateTime && tempDateTime) {
      const selectedDateTime = toJsDate(tempDateTime);
      if (!isNaN(selectedDateTime.getTime())) {
        const now = new Date();
        const oneMinuteFromNow = new Date(now.getTime() + 60000);
        hasValidDateTime = selectedDateTime >= oneMinuteFromNow;
      }
    } else {
      hasValidDateTime = !!displayData.suggestedDateTime;
    }

    // Check duration (either saved or being edited, considering defaults)
    let hasValidDuration = false;
    let currentDuration = displayData.duration || 60; // Default to 60 if not set
    if (isEditingDuration) {
      if (
        !tempDurationInput ||
        tempDurationInput.trim() === "" ||
        isNaN(Number(tempDurationInput))
      ) {
        currentDuration = 60; // default
      } else {
        currentDuration = Number(tempDurationInput);
      }
    }
    hasValidDuration =
      !!currentDuration && currentDuration >= 15 && currentDuration <= 1440;

    // Check group size (either saved or being edited, considering defaults)
    let hasValidGroupSize = false;
    let currentGroupSize = displayData.groupSize || 2; // Default to 2 if not set
    if (isEditingGroupSize) {
      if (
        !tempGroupSizeInput ||
        tempGroupSizeInput.trim() === "" ||
        isNaN(Number(tempGroupSizeInput))
      ) {
        currentGroupSize = 2; // default
      } else {
        currentGroupSize = Number(tempGroupSizeInput);
      }
    }
    hasValidGroupSize =
      !!currentGroupSize && currentGroupSize >= 2 && currentGroupSize <= 50;

    // Check category (either saved or being edited)
    const hasValidCategory = isEditingCategory
      ? !!tempCategory
      : !!displayData.category;

    return {
      hasTitle,
      hasLocation,
      hasValidDateTime,
      hasValidDuration,
      hasValidGroupSize,
      hasValidCategory,
      currentDuration,
      currentGroupSize,
    };
  };

  // Check if all required fields are completed (including unsaved edits and defaults)
  const isReadyToPost = () => {
    const status = getFieldStatus();
    return !!(
      status.hasTitle &&
      status.hasLocation &&
      status.hasValidDateTime &&
      status.hasValidDuration &&
      status.hasValidGroupSize &&
      status.hasValidCategory
    );
  };

  // Get smart missing field messages
  const getMissingFieldsMessage = () => {
    const status = getFieldStatus();
    const missing = [];
    const suggestions = [];

    if (!status.hasTitle) {
      missing.push("title");
    }

    if (!status.hasLocation) {
      if (displayData.location?.trim() && !displayData.coordinates) {
        missing.push("verified location (please select from map suggestions)");
      } else {
        missing.push("location");
      }
    }

    if (!status.hasValidDateTime) {
      missing.push("date/time");
    }

    if (!status.hasValidCategory) {
      missing.push("category");
    }

    // For duration and group size, give smart suggestions
    if (!status.hasValidDuration) {
      missing.push("valid duration");
    } else if (!displayData.duration && status.currentDuration === 60) {
      // Using default duration, suggest confirmation
      suggestions.push("confirm 1-hour duration");
    }

    if (!status.hasValidGroupSize) {
      missing.push("valid group size");
    } else if (!displayData.groupSize && status.currentGroupSize === 2) {
      // Using default group size, suggest confirmation
      suggestions.push("confirm 2-person group");
    }

    let message = "";
    if (missing.length > 0) {
      message += `Missing: ${missing.join(", ")}`;
    }
    if (suggestions.length > 0) {
      if (message) message += ". ";
      message += `Please ${suggestions.join(" and ")}.`;
    }

    return message;
  };

  const handleButtonClick = async () => {
    if (isProposal && onCreateFromProposal) {
      await onCreateFromProposal();
      return;
    }

    // Auto-save any unsaved edits before posting
    const autoSavedData = { ...displayData };

    // Auto-save title if it's being edited
    if (isEditingTitle && tempTitle.trim()) {
      autoSavedData.title = tempTitle.trim();
    }

    // Auto-save description if it's being edited
    if (isEditingDescription) {
      autoSavedData.description = tempDescription.trim();
    }

    // Auto-save date/time if it's being edited
    if (isEditingDateTime && tempDateTime) {
      const selectedDateTime = toJsDate(tempDateTime);
      if (!isNaN(selectedDateTime.getTime())) {
        const now = new Date();
        const oneMinuteFromNow = new Date(now.getTime() + 60000);
        if (selectedDateTime >= oneMinuteFromNow) {
          autoSavedData.suggestedDateTime = selectedDateTime.toISOString();
        }
      }
    }

    // Auto-save category if it's being edited
    if (isEditingCategory && tempCategory) {
      autoSavedData.category = tempCategory;
    }

    // Auto-save duration if it's being edited
    if (isEditingDuration) {
      let finalDuration = tempDuration;
      if (
        !tempDurationInput ||
        tempDurationInput.trim() === "" ||
        isNaN(Number(tempDurationInput))
      ) {
        finalDuration = 60;
      } else {
        finalDuration = Number(tempDurationInput);
      }
      if (finalDuration >= 15 && finalDuration <= 1440) {
        autoSavedData.duration = finalDuration;
      }
    }

    // Auto-save group size if it's being edited
    if (isEditingGroupSize) {
      let finalGroupSize = tempGroupSize;
      if (
        !tempGroupSizeInput ||
        tempGroupSizeInput.trim() === "" ||
        isNaN(Number(tempGroupSizeInput))
      ) {
        finalGroupSize = 2;
      } else {
        finalGroupSize = Number(tempGroupSizeInput);
      }
      if (finalGroupSize >= 2 && finalGroupSize <= 50) {
        autoSavedData.groupSize = finalGroupSize;
      }
    }

    // Update the data with auto-saved values
    onUpdate(autoSavedData);

    // Reset all editing states
    setIsEditingTitle(false);
    setIsEditingDescription(false);
    setIsEditingDateTime(false);
    setIsEditingDuration(false);
    setIsEditingGroupSize(false);

    // Check if all required fields are completed with the auto-saved data
    const isComplete = !!(
      autoSavedData.title?.trim() &&
      autoSavedData.location?.trim() &&
      autoSavedData.suggestedDateTime &&
      autoSavedData.duration &&
      autoSavedData.duration >= 15 &&
      autoSavedData.groupSize &&
      autoSavedData.groupSize >= 2
    );

    if (!isComplete) {
      addToast("Please complete all required fields before posting.", "error");
      return;
    }

    if (!onPost) {
      addToast("Post functionality not available.", "error");
      return;
    }

    setIsPosting(true);
    try {
      await onPost(autoSavedData);
    } catch (error) {
      crashlytics.logError(
        `Error posting activity: ${getErrorMessage(error)}`,
        "CreatePage.tsx",
        error,
      );
      addToast("Failed to post activity. Please try again.", "error");
    } finally {
      setIsPosting(false);
    }
  };

  const formatDate = (dateInput?: any) => {
    const date = toJsDate(dateInput);
    if (!date || isNaN(date.getTime())) return "Not set";
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes || minutes <= 0) return "";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    let result = "";
    if (h > 0) result += `${h}h`;
    if (m > 0) result += `${h > 0 ? " " : ""}${m}m`;
    return result;
  };

  const getLocalDateTimeForInput = (isoDate: string) => {
    try {
      const date = toJsDate(isoDate);
      if (isNaN(date.getTime())) {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset() + 1); // Add 1 minute buffer
        return now.toISOString().slice(0, 16);
      }
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      return date.toISOString().slice(0, 16);
    } catch {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset() + 1);
      return now.toISOString().slice(0, 16);
    }
  };

  const handleTitleSave = () => {
    const trimmedTitle = tempTitle.trim();
    if (!trimmedTitle) {
      addToast("Title cannot be empty.", "error");
      return;
    }
    if (trimmedTitle.length > 100) {
      addToast("Title must be 100 characters or less.", "error");
      return;
    }
    addToGlobalManualEdits("title"); // Mark as manually edited
    onUpdate({ ...displayData, title: trimmedTitle });
    setIsEditingTitle(false);
    addToast("Title updated!", "success");
  };

  const handleDescriptionSave = () => {
    const trimmedDescription = tempDescription.trim();
    if (trimmedDescription.length > 500) {
      addToast("Description must be 500 characters or less.", "error");
      return;
    }
    addToGlobalManualEdits("description"); // Mark as manually edited
    onUpdate({ ...displayData, description: trimmedDescription });
    setIsEditingDescription(false);
    addToast("Description updated!", "success");
  };

  const handleDateTimeSave = () => {
    if (!tempDateTime) {
      addToast("Please select a date and time.", "error");
      return;
    }

    const selectedDateTime = toJsDate(tempDateTime);
    if (isNaN(selectedDateTime.getTime())) {
      addToast("Please select a valid date and time.", "error");
      return;
    }

    // Enhanced validation for date/time selection (same as manual form)
    const now = new Date();
    const oneMinuteFromNow = new Date(now.getTime() + 60000); // 1 minute buffer
    const oneYearFromNow = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year max

    if (selectedDateTime < oneMinuteFromNow) {
      addToast(
        "Activity must be scheduled at least 1 minute in the future.",
        "error",
      );
      return;
    }

    if (selectedDateTime > oneYearFromNow) {
      addToast(
        "Activity cannot be scheduled more than 1 year in advance.",
        "error",
      );
      return;
    }

    // Check if the selected time is during reasonable hours (optional warning)
    const hour = selectedDateTime.getHours();
    if (hour < 6 || hour > 23) {
      addToast(
        "Activity scheduled during late/early hours. Consider choosing a more suitable time.",
        "info",
      );
      // Don't block creation, just warn
    }

    onUpdate({
      ...displayData,
      suggestedDateTime: selectedDateTime.toISOString(),
    });
    setIsEditingDateTime(false);
    addToGlobalManualEdits("dateTime"); // Mark as manually edited
    addToast("Date & time updated!", "success");
  };

  const handleDurationSave = () => {
    let finalDuration = tempDuration;

    // If input is empty or invalid, use default
    if (
      !tempDurationInput ||
      tempDurationInput.trim() === "" ||
      isNaN(Number(tempDurationInput))
    ) {
      finalDuration = 60;
    } else {
      finalDuration = Number(tempDurationInput);
    }

    if (finalDuration < 15) {
      addToast("Duration must be at least 15 minutes.", "error");
      return;
    }
    if (finalDuration > 1440) {
      // 24 hours max
      addToast("Duration cannot exceed 24 hours.", "error");
      return;
    }

    setTempDuration(finalDuration);
    setTempDurationInput(String(finalDuration));
    addToGlobalManualEdits("duration"); // Mark as manually edited
    onUpdate({ ...displayData, duration: finalDuration });
    setIsEditingDuration(false);
    addToast("Duration updated!", "success");
  };

  const handleGroupSizeSave = () => {
    let finalGroupSize = tempGroupSize;

    // If input is empty or invalid, use default
    if (
      !tempGroupSizeInput ||
      tempGroupSizeInput.trim() === "" ||
      isNaN(Number(tempGroupSizeInput))
    ) {
      finalGroupSize = 2;
    } else {
      finalGroupSize = Number(tempGroupSizeInput);
    }

    if (finalGroupSize < 2) {
      addToast("Group size must be at least 2 people.", "error");
      return;
    }
    if (finalGroupSize > 50) {
      // Reasonable maximum
      addToast("Group size cannot exceed 50 people.", "error");
      return;
    }

    setTempGroupSize(finalGroupSize);
    setTempGroupSizeInput(String(finalGroupSize));
    addToGlobalManualEdits("groupSize"); // Mark as manually edited
    onUpdate({ ...displayData, groupSize: finalGroupSize });
    setIsEditingGroupSize(false);
    addToast("Group size updated!", "success");
  };

  const handleCategorySave = () => {
    if (!tempCategory) {
      addToast("Please select a category.", "error");
      return;
    }

    addToGlobalManualEdits("category"); // Mark as manually edited
    onUpdate({ ...displayData, category: tempCategory });
    setIsEditingCategory(false);
    addToast("Category updated!", "success");
  };

  const inputTitleRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Android automatically handles this - iOS needs manual scrolling
    if (isAndroid || !isEditingTitle) return;

    const titleInput = inputTitleRef.current;
    if (titleInput) {
      titleInput.focus();
      // Wait for the keyboard animation to begin
      setTimeout(() => {
        titleInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [isAndroid, isEditingTitle]);

  const inputDescriptionRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    // Android automatically handles this - iOS needs manual scrolling
    if (isAndroid || !isEditingDescription) return;

    const descriptionInput = inputDescriptionRef.current;
    if (descriptionInput) {
      descriptionInput.focus();
      // Wait for the keyboard animation to begin
      setTimeout(() => {
        descriptionInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [isAndroid, isEditingDescription]);

  const dateTimeInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Android automatically handles this - iOS needs manual scrolling
    if (isAndroid || !isEditingDateTime) return;

    const dateInput = dateTimeInputRef.current;
    if (dateInput) {
      dateInput.focus();
      // Wait for the keyboard animation to begin
      setTimeout(() => {
        dateInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [isAndroid, isEditingDateTime]);

  const durationInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Android automatically handles this - iOS needs manual scrolling
    if (isAndroid || !isEditingDuration) return;

    const durationInput = durationInputRef.current;
    if (durationInput) {
      durationInput.focus();
      // Wait for the keyboard animation to begin
      setTimeout(() => {
        durationInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [isAndroid, isEditingDuration]);

  const groupSizeInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // Android automatically handles this - iOS needs manual scrolling
    if (isAndroid || !isEditingGroupSize) return;

    const groupInput = groupSizeInputRef.current;
    if (groupInput) {
      groupInput.focus();
      // Wait for the keyboard animation to begin
      setTimeout(() => {
        groupInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  }, [isAndroid, isEditingGroupSize]);

  return (
    <>
      <div className="mt-2 border-t border-gray-200 pt-3 space-y-2 text-sm text-gray-900">
        {/* Title */}
        <div className="flex items-center justify-between">
          {isEditingTitle ? (
            <div className="flex items-center gap-2 w-full">
              <div className="flex items-center gap-2 flex-1">
                <input
                  ref={inputTitleRef}
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  placeholder="Activity title"
                  maxLength={100}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTitleSave();
                    if (e.key === "Escape") setIsEditingTitle(false);
                  }}
                />
                <span className="text-xs text-gray-400">
                  {tempTitle.length}/100
                </span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={handleTitleSave}
                  className="p-1 rounded bg-primary text-white hover:opacity-90"
                >
                  <CheckIcon className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setIsEditingTitle(false)}
                  className="p-1 rounded bg-gray-300 text-gray-600 hover:bg-gray-400"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              {displayData.title ? (
                <h4
                  className={`font-bold truncate ${disabled ? "text-gray-300" : "text-gray-900"}`}
                >
                  {displayData.title}
                </h4>
              ) : disabled ? (
                <h4 className="font-medium text-gray-300 italic">
                  No title added
                </h4>
              ) : (
                <h4 className="font-medium text-gray-500 italic">
                  Click to add activity title
                </h4>
              )}
              {!disabled && (
                <button
                  onClick={() => setIsEditingTitle(true)}
                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors"
                  aria-label="Edit title"
                >
                  <Edit2Icon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Category */}
        <div className="flex items-center justify-between">
          {isEditingCategory ? (
            <div className="flex items-center gap-2 w-full">
              <select
                value={tempCategory}
                onChange={(e) =>
                  setTempCategory(getValidCategory(e.target.value))
                }
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <button
                onClick={handleCategorySave}
                className="p-1 rounded bg-primary text-white hover:opacity-90"
              >
                <CheckIcon className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  setIsEditingCategory(false);
                  setTempCategory(
                    getValidCategory(displayData.category || "outdoor"),
                  );
                }}
                className="p-1 rounded bg-gray-300 text-gray-600 hover:bg-gray-400"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              {displayData.category ? (
                <div className="flex items-center gap-2">
                  {(() => {
                    const categoryInfo = CATEGORIES.find(
                      (c) => c.value === displayData.category,
                    );
                    return categoryInfo ? (
                      <div
                        className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${disabled ? "bg-gray-300 text-white opacity-50" : "bg-primary/10 text-primary"}`}
                      >
                        <categoryInfo.Icon className="w-3 h-3 mr-1" />
                        {categoryInfo.name}
                      </div>
                    ) : (
                      <span
                        className={`font-medium ${disabled ? "text-gray-300" : "text-gray-900"}`}
                      >
                        {displayData.category
                          ? displayData.category
                          : "Unknown"}
                      </span>
                    );
                  })()}
                  {displayData.isPrivate && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${disabled ? "bg-gray-200 text-gray-400" : "bg-purple-100 text-purple-700"}`}
                    >
                      🔒 Private
                    </span>
                  )}
                </div>
              ) : disabled ? (
                <span className="text-gray-300 italic">
                  No category selected
                </span>
              ) : (
                <span className="text-gray-500 italic">
                  Click to select category
                </span>
              )}
              {!disabled && (
                <button
                  onClick={() => {
                    setIsEditingCategory(true);
                    setTempCategory(getValidCategory(displayData.category));
                  }}
                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors"
                  aria-label="Edit category"
                >
                  <Edit2Icon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Image */}
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            {selectedImageUrl ? (
              <div className="flex items-center">
                <div className="w-16 h-16 rounded-lg overflow-hidden mr-3 border border-gray-200">
                  <img
                    src={selectedImageUrl}
                    onError={(e) => {
                      e.currentTarget.src = fallBackImage;
                    }}
                    alt="Activity"
                    className={`w-full h-full object-cover ${disabled ? "opacity-50" : ""}`}
                  />
                </div>
                <span
                  className={`text-sm ${disabled ? "text-gray-300" : "text-gray-600"}`}
                >
                  Activity photo selected
                </span>
              </div>
            ) : (
              <div
                className={`flex items-center italic ${disabled ? "text-gray-300 opacity-50" : "text-gray-500"}`}
              >
                <CameraIcon className="w-4 h-4 mr-2" />
                <span>No photo selected</span>
              </div>
            )}
          </div>
          {!disabled && (
            <div className="flex items-center gap-1">
              {/* Refresh image button */}
              <button
                onClick={() => {
                  // Clear the imageFetchedFor to allow refetch
                  setImageFetchedFor("");
                  setSelectedImageUrl("");
                  // Trigger a new fetch by updating the dependency
                  const activityIdentity = `${displayData.category}-${displayData.title}`;
                  getActivityImageUrl(
                    displayData.category as ActivityCategory,
                    displayData.title,
                    displayData.description || "",
                  )
                    .then((url) => {
                      setSelectedImageUrl(url);
                      setImageFetchedFor(activityIdentity);
                      onUpdate({ ...displayData, imageUrl: url });
                    })
                    .catch((error) => {
                      crashlytics.logError(
                        `Error refreshing activity image: ${getErrorMessage(error)}`,
                        "CreatePage.tsx",
                        error,
                      );
                    });
                }}
                className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors"
                aria-label="Refresh photo"
                title="Get a different photo"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
              {/* Change photo button */}
              <button
                onClick={() => setShowImageModal(true)}
                className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors"
                aria-label="Change photo"
              >
                <ImageIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="flex items-start justify-between">
          {isEditingDescription ? (
            <div className="flex items-start gap-2 w-full">
              <div className="flex-1">
                <textarea
                  ref={inputDescriptionRef}
                  value={tempDescription}
                  onChange={(e) => setTempDescription(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  rows={2}
                  placeholder="Activity description (optional)"
                  maxLength={500}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.ctrlKey) handleDescriptionSave();
                    if (e.key === "Escape") setIsEditingDescription(false);
                  }}
                />
                <p className="text-right text-xs text-gray-400 mt-1">
                  {tempDescription.length}/500
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={handleDescriptionSave}
                  className="p-1 rounded bg-primary text-white hover:opacity-90"
                >
                  <CheckIcon className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setIsEditingDescription(false)}
                  className="p-1 rounded bg-gray-300 text-gray-600 hover:bg-gray-400"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between w-full">
              {displayData.description ? (
                <p
                  className={` ${disabled ? "text-gray-300" : "text-gray-700"} pb-1 flex-1`}
                >
                  {displayData.description}
                </p>
              ) : disabled ? (
                <p className="text-gray-300 pb-1 flex-1 italic">
                  No description provided
                </p>
              ) : (
                <p className="text-gray-400 pb-1 flex-1 italic">
                  Click to add description (optional)
                </p>
              )}
              {!disabled && (
                <button
                  onClick={() => setIsEditingDescription(true)}
                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors ml-2 flex-shrink-0"
                  aria-label="Edit description"
                >
                  <Edit2Icon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Location */}
        <div className="flex items-center gap-2">
          <MapPinIcon
            className={`w-4 h-4 mr-1 flex-shrink-0 ${disabled ? "text-gray-300" : "text-primary/80"}`}
          />
          {isEditingLocation && isApiLoaded ? (
            <LocationAutocomplete
              initialValue={displayData.location || ""}
              onLocationSelect={async (place) => {
                setIsEditingLocation(false);
                addToGlobalManualEdits("location"); // Mark as manually edited
                addToast("Verifying location...", "info");
                try {
                  const details = await getPlaceDetails(place.placeId);
                  onUpdate({
                    ...displayData,
                    location: details.formattedAddress,
                    placeId: place.placeId,
                    coordinates: details.coordinates,
                  });
                  addToast("Location updated!", "success");
                } catch (error) {
                  const message =
                    error instanceof Error
                      ? error.message
                      : "Could not retrieve details for that location.";
                  addToast(message, "error");
                }
              }}
              onFocus={(locationInputRef) => {
                // Android automatically handles this - iOS needs manual scrolling
                if (isAndroid) return;
                if (locationInputRef && locationInputRef.current) {
                  locationInputRef.current.focus();
                  // Wait for the keyboard animation to begin
                  setTimeout(() => {
                    locationInputRef.current.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  }, 100);
                }
              }}
              onBlur={() => {
                let currLocation = displayData.location || "";
                if (isEditingLocation || !currLocation.trim()) {
                  setIsEditingLocation(false);
                  return;
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-between w-full min-w-0">
              {displayData.location ? (
                <div
                  className={`flex-1 flex items-center gap-2 truncate ${disabled ? "text-gray-300" : "text-gray-700"}`}
                >
                  <span
                    className={`truncate ${disabled ? "text-gray-300" : "font-medium  text-gray-700"}`}
                  >
                    {displayData.location}
                  </span>
                  {!disabled && !displayData.coordinates && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full font-medium">
                      Needs verification
                    </span>
                  )}
                  {!disabled && displayData.coordinates && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                      ✓ Verified
                    </span>
                  )}
                </div>
              ) : disabled ? (
                <span className="text-gray-300 italic">No location set</span>
              ) : (
                <span className="text-gray-400 italic">
                  Click to select location
                </span>
              )}
              {!disabled && (
                <button
                  onClick={() => setIsEditingLocation(true)}
                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors ml-2"
                  aria-label="Edit location"
                >
                  <Edit2Icon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Date & Time */}
        <div className="flex items-center gap-2">
          <ClockIcon
            className={`w-4 h-4 mr-1 flex-shrink-0 ${disabled ? "text-gray-300" : "text-primary/80"}`}
          />
          {isEditingDateTime ? (
            <div className="flex items-center gap-2 w-full">
              <div className="flex items-center gap-2 flex-1">
                <input
                  ref={dateTimeInputRef}
                  type="datetime-local"
                  value={getLocalDateTimeForInput(tempDateTime)}
                  onChange={(e) =>
                    setTempDateTime(toJsDate(e.target.value).toISOString())
                  }
                  // className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                  autoFocus
                  disabled={isEditingDuration || disabled}
                />
              </div>
              <div className="flex gap-1">
                <button
                  onClick={handleDateTimeSave}
                  className="p-1 rounded bg-primary text-white hover:opacity-90"
                >
                  <CheckIcon className="w-3 h-3" />
                </button>
                <button
                  onClick={() => setIsEditingDateTime(false)}
                  className="p-1 rounded bg-gray-300 text-gray-600 hover:bg-gray-400"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <div
                className={`flex items-center ${disabled ? "text-gray-300" : "font-medium text-gray-700"}`}
              >
                <span>{formatDate(displayData.suggestedDateTime)}</span>
                {displayData.duration && displayData.duration > 0 && (
                  <span
                    className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${disabled ? "bg-gray-100 text-gray-300" : "bg-gray-100 text-gray-500 font-semibold"}`}
                  >
                    {formatDuration(displayData.duration)}
                  </span>
                )}
              </div>
              {!disabled && (
                <button
                  onClick={() => setIsEditingDateTime(true)}
                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors ml-2"
                  aria-label="Edit date and time"
                >
                  <Edit2Icon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Duration */}
        <div className="flex items-center gap-2">
          <ClockIcon
            className={`w-4 h-4 mr-1 flex-shrink-0 ${disabled ? "text-gray-300" : "text-primary/80"}`}
          />
          {isEditingDuration ? (
            <div className="flex items-center gap-2 w-full">
              <input
                ref={durationInputRef}
                type="number"
                value={tempDurationInput}
                onChange={(e) => {
                  setTempDurationInput(e.target.value);
                  const numValue = Number(e.target.value);
                  if (!isNaN(numValue) && numValue > 0) {
                    setTempDuration(numValue);
                  }
                }}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                min="15"
                max="1440"
                step="15"
                placeholder="60"
                autoFocus
                disabled={isEditingDateTime || disabled}
              />
              <span className="text-sm text-gray-500">minutes</span>
              <button
                onClick={handleDurationSave}
                className="p-1 rounded bg-primary text-white hover:opacity-90"
              >
                <CheckIcon className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  setIsEditingDuration(false);
                  setTempDurationInput(String(displayData.duration));
                  setTempDuration(displayData.duration);
                }}
                className="p-1 rounded bg-gray-300 text-gray-600 hover:bg-gray-400"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <span
                className={`${disabled ? "text-gray-300" : "font-medium text-gray-900"}`}
              >
                Duration: {formatDuration(displayData.duration) || "Not set"}
              </span>
              {!disabled && (
                <button
                  onClick={() => {
                    setIsEditingDuration(true);
                    setTempDurationInput(String(displayData.duration));
                  }}
                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors ml-2"
                  aria-label="Edit duration"
                >
                  <Edit2Icon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Group Size */}
        <div className="flex items-center gap-2">
          <UsersIcon
            className={`w-4 h-4 mr-1 flex-shrink-0 ${disabled ? "text-gray-300" : "text-primary/80"}`}
          />
          {isEditingGroupSize ? (
            <div className="flex items-center gap-2 w-full">
              <input
                ref={groupSizeInputRef}
                type="number"
                value={tempGroupSizeInput}
                onChange={(e) => {
                  setTempGroupSizeInput(e.target.value);
                  const numValue = Number(e.target.value);
                  if (!isNaN(numValue) && numValue > 0) {
                    setTempGroupSize(numValue);
                  }
                }}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                min="2"
                max="50"
                placeholder="2"
                autoFocus
              />
              <span className="text-sm text-gray-500">people</span>
              <button
                onClick={handleGroupSizeSave}
                className="p-1 rounded bg-primary text-white hover:opacity-90"
              >
                <CheckIcon className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  setIsEditingGroupSize(false);
                  setTempGroupSizeInput(String(displayData.groupSize));
                  setTempGroupSize(displayData.groupSize);
                }}
                className="p-1 rounded bg-gray-300 text-gray-600 hover:bg-gray-400"
              >
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between w-full">
              <span
                className={`${disabled ? "text-gray-300" : "font-medium text-gray-900"}`}
              >
                For {displayData.groupSize} people
              </span>
              {!disabled && (
                <button
                  onClick={() => {
                    setIsEditingGroupSize(true);
                    setTempGroupSizeInput(String(displayData.groupSize));
                  }}
                  className="p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-primary transition-colors ml-2"
                  aria-label="Edit group size"
                >
                  <Edit2Icon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Invitees Management - Only show when creating from proposal */}
        {!disabled && onPost && (
          <div className="mt-4 pt-3 border-t border-gray-200 space-y-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Invite people (optional)
                </h3>
                <button
                  onClick={() => setShowManageInviteesModal?.(true)}
                  className="text-xs px-2 py-1 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                >
                  Invite
                </button>
              </div>
              {proposalInviteeUsers.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {proposalInviteeUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-gray-200 text-sm"
                    >
                      <img
                        src={
                          user.profileImageUrl ||
                          `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || "User")}&background=random`
                        }
                        alt={user.username}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-gray-700">{user.username}</span>
                      <button
                        onClick={() => handleRemoveInvitee?.(user.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post Activity Button */}
        {!disabled && onPost && (
          <div className="mt-4 pt-3 border-t border-gray-200 space-y-3">
            {/* Privacy Toggle */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex-1">
                <div className="flex items-center">
                  <span className="text-sm font-medium text-gray-900">
                    Private Activity
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {displayData.isPrivate
                    ? "Only visible via link. You can invite specific users."
                    : "Visible to everyone on home feed and map."}
                </p>
              </div>
              <button
                onClick={() =>
                  onUpdate({
                    ...displayData,
                    isPrivate: !displayData.isPrivate,
                  })
                }
                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                  displayData.isPrivate ? "bg-primary" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block w-4 h-4 transform transition-transform bg-white rounded-full ${
                    displayData.isPrivate ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            <button
              onClick={handleButtonClick}
              disabled={
                !isReadyToPost() ||
                isPosting ||
                (isProposal && isCreatingFromProposal)
              }
              className={`w-full py-3 px-4 rounded-lg font-semibold text-base transition-all duration-200 min-h-[48px] touch-manipulation ${
                isReadyToPost() &&
                !isPosting &&
                !(isProposal && isCreatingFromProposal)
                  ? "bg-primary text-white hover:bg-primary/90 hover:shadow-md active:scale-95"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
              style={{ WebkitTapHighlightColor: "transparent" }}
            >
              {isProposal ? (
                isCreatingFromProposal ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating & Inviting...
                  </div>
                ) : isReadyToPost() ? (
                  `🎉 Create & Invite${proposalInviteeLabel ? ` (${proposalInviteeLabel})` : " friends"}`
                ) : getMissingFieldsMessage() ? (
                  "Complete fields to create"
                ) : (
                  "Create & Invite"
                )
              ) : isPosting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Posting Activity...
                </div>
              ) : isReadyToPost() ? (
                hasInvitees ? (
                  `🎉 Post & Invite${proposalInviteeLabel ? ` (${proposalInviteeLabel})` : ""}`
                ) : (
                  "🎉 Post Activity"
                )
              ) : getMissingFieldsMessage() ? (
                "Complete fields to post"
              ) : (
                "Post Activity"
              )}
            </button>
            {!isReadyToPost() && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                {getMissingFieldsMessage()}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Image Selection Modal */}
      <Portal>
        <ActivityImageSelectionModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          onSelect={handleImageSelect}
          category={displayData.category as ActivityCategory}
          title={displayData.title}
          description={displayData.description}
          location={displayData.location}
          currentImageUrl={selectedImageUrl}
        />
      </Portal>

      {/* Manage Invitees Modal */}
      <Portal>
        {showManageInviteesModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Add Invitees
                </h2>
                <button
                  onClick={() => {
                    setShowManageInviteesModal?.(false);
                    setInviteeSearchQuery?.("");
                    setInviteeSearchResults?.([]);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Search Input */}
                <input
                  type="text"
                  placeholder="Search users to invite..."
                  value={inviteeSearchQuery}
                  onChange={(e) => handleSearchInvitees?.(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />

                {inviteeSearchIsLoading && (
                  <p className="text-sm text-gray-500 py-3 text-center">
                    Loading users...
                  </p>
                )}

                {/* Current Invitees Section */}
                {proposalInviteeUsers && proposalInviteeUsers.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Invited ({proposalInviteeUsers.length})
                    </h3>
                    <div className="space-y-2">
                      {proposalInviteeUsers.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-2 bg-blue-50 rounded-lg border border-blue-100"
                        >
                          <div className="flex items-center gap-2">
                            <img
                              src={
                                user.profileImageUrl ||
                                `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || "User")}&background=random`
                              }
                              alt={user.username}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {user.username}
                              </p>
                              {user.location && (
                                <p className="text-xs text-gray-600">
                                  {user.location}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveInvitee?.(user.id)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested Users or Search Results */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    {hasInviteeSearchQuery ? "Search Results" : "Suggested"}
                  </h3>
                  {inviteeSearchResults && inviteeSearchResults.length > 0 ? (
                    <div className="space-y-2">
                      {inviteeSearchResults.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg border border-gray-100 cursor-pointer"
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <img
                              src={
                                user.profileImageUrl ||
                                `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username || "User")}&background=random`
                              }
                              alt={user.username}
                              className="w-8 h-8 rounded-full"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {user.username}
                              </p>
                              {user.location && (
                                <p className="text-xs text-gray-600">
                                  {user.location}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddInvitee?.(user.id)}
                            className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary/90 transition-colors"
                          >
                            Add
                          </button>
                        </div>
                      ))}
                      {inviteeSearchHasMore && (
                        <div ref={inviteeLoadMoreTriggerRef} className="h-8" />
                      )}
                      {inviteeSearchIsLoadingMore && (
                        <p className="text-xs text-gray-500 text-center py-2">
                          Loading more...
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      {hasInviteeSearchQuery
                        ? "No users found"
                        : "Type a username to search for people to invite"}
                    </p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-4 sticky bottom-0 bg-white">
                <button
                  onClick={() => {
                    setShowManageInviteesModal?.(false);
                    setInviteeSearchQuery?.("");
                    setInviteeSearchResults?.([]);
                  }}
                  className="w-full py-2 px-4 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </Portal>
    </>
  );
};

const AIBubble: React.FC<{
  message: AIMessage;
  onUpdate: (timestamp: string, newData: ParsedAIResponse) => Promise<void>;
  isApiLoaded: boolean;
  addToast: (message: string, type: ToastType) => void;
  onPost?: (data: ParsedAIResponse) => void;
  addToGlobalManualEdits: (fieldName: string) => void;
  onEditingInline?: (editingInline: boolean) => void;
  disabled?: boolean;
  onCreateFromProposal?: () => Promise<void>;
  isCreatingFromProposal?: boolean;
  proposalInviteeLabel?: string;
  proposalInviteeUsers?: User[];
  showManageInviteesModal?: boolean;
  setShowManageInviteesModal?: (value: boolean) => void;
  inviteeSearchQuery?: string;
  setInviteeSearchQuery?: (value: string) => void;
  inviteeSearchResults?: User[];
  setInviteeSearchResults?: (value: User[]) => void;
  inviteeSearchIsLoading?: boolean;
  inviteeSearchIsLoadingMore?: boolean;
  inviteeSearchHasMore?: boolean;
  loadMoreInviteeSearch?: () => void;
  handleRemoveInvitee?: (userId: string) => void;
  handleAddInvitee?: (userId: string) => void;
  handleSearchInvitees?: (query: string) => void;
}> = ({
  message,
  onUpdate,
  isApiLoaded,
  addToast,
  onPost,
  addToGlobalManualEdits,
  onEditingInline,
  disabled,
  onCreateFromProposal,
  isCreatingFromProposal,
  proposalInviteeLabel,
  proposalInviteeUsers,
  showManageInviteesModal,
  setShowManageInviteesModal,
  inviteeSearchQuery,
  setInviteeSearchQuery,
  inviteeSearchResults,
  setInviteeSearchResults,
  inviteeSearchIsLoading,
  inviteeSearchIsLoadingMore,
  inviteeSearchHasMore,
  loadMoreInviteeSearch,
  handleRemoveInvitee,
  handleAddInvitee,
  handleSearchInvitees,
}) => {
  const { content, isTyping, timestamp } = message;

  // Use useMemo to properly track content changes
  const { text, reviewData } = React.useMemo(() => {
    let text: React.ReactNode;
    let reviewData: ParsedAIResponse | null = null;

    if (typeof content === "string") {
      text = content;
    } else if (content && "followUpQuestions" in content) {
      reviewData = content;
      text = content.followUpQuestions?.[0] || "Here is the plan:";
    } else {
      text = "I'm having a little trouble thinking. Could you try again?";
    }

    return { text, reviewData };
  }, [content, timestamp]);

  const handleActivityUpdate = async (newData: ParsedAIResponse) => {
    await onUpdate(timestamp, newData);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      className="flex items-start gap-3 w-full max-w-full overflow-hidden"
    >
      <div className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0 flex items-center justify-center border border-gray-200">
        <AIIcon className="w-5 h-5 text-primary" />
      </div>
      <div className="flex flex-col gap-3 flex-1 min-w-0">
        <div className="bg-white text-gray-900 rounded-t-2xl rounded-br-2xl py-2 px-4 max-w-xs md:max-w-md shadow-sm border border-gray-200">
          {isTyping && !text ? (
            <div className="flex items-center space-x-1 py-2">
              <span
                className="w-2 h-2 bg-primary/50 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              ></span>
              <span
                className="w-2 h-2 bg-primary/50 rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              ></span>
              <span
                className="w-2 h-2 bg-primary/50 rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              ></span>
            </div>
          ) : (
            <p className="text-base break-words whitespace-pre-wrap">{text}</p>
          )}
        </div>
        {reviewData && (
          <div className={`w-full max-w-sm md:max-w-lg`}>
            <ActivityReviewCard
              key={`form-${timestamp}-${JSON.stringify(reviewData).substring(0, 50)}`}
              data={reviewData}
              onUpdate={handleActivityUpdate}
              isApiLoaded={isApiLoaded}
              addToast={addToast}
              onPost={onPost}
              addToGlobalManualEdits={addToGlobalManualEdits}
              onEditingInline={onEditingInline}
              disabled={disabled}
              onCreateFromProposal={onCreateFromProposal}
              isCreatingFromProposal={isCreatingFromProposal}
              proposalInviteeLabel={proposalInviteeLabel}
              proposalInviteeUsers={proposalInviteeUsers}
              showManageInviteesModal={showManageInviteesModal}
              setShowManageInviteesModal={setShowManageInviteesModal}
              inviteeSearchQuery={inviteeSearchQuery}
              setInviteeSearchQuery={setInviteeSearchQuery}
              inviteeSearchResults={inviteeSearchResults}
              setInviteeSearchResults={setInviteeSearchResults}
              inviteeSearchIsLoading={inviteeSearchIsLoading}
              inviteeSearchIsLoadingMore={inviteeSearchIsLoadingMore}
              inviteeSearchHasMore={inviteeSearchHasMore}
              loadMoreInviteeSearch={loadMoreInviteeSearch}
              handleRemoveInvitee={handleRemoveInvitee}
              handleAddInvitee={handleAddInvitee}
              handleSearchInvitees={handleSearchInvitees}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

const SuggestedReply: React.FC<{
  text: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ text, onClick, disabled = false }) => (
  <motion.button
    layout
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`px-4 py-2 text-sm font-semibold bg-white border border-gray-200 rounded-full text-primary hover:bg-primary/5 hover:border-primary/50 transition-all duration-200 shadow-sm ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  >
    {text}
  </motion.button>
);

async function* generateActivitySuggestionStreamFromCallable(
  conversationHistory: AIMessage[],
  signal?: AbortSignal,
): AsyncGenerator<string, void, unknown> {
  if (signal?.aborted) {
    yield JSON.stringify({
      followUpQuestions: ["Let's try that again. What would you like to do?"],
      suggestedReplies: [
        "Coffee & chat",
        "Outdoor activity",
        "Entertainment",
        "Something different",
      ],
    });
    return;
  }

  try {
    const result = await generateActivitySuggestionCallable({
      conversationHistory,
    });

    if (signal?.aborted) {
      yield JSON.stringify({
        followUpQuestions: [
          "Let's try that again. What would you like to do?",
        ],
        suggestedReplies: [
          "Coffee & chat",
          "Outdoor activity",
          "Entertainment",
          "Something different",
        ],
      });
      return;
    }

    yield JSON.stringify(result.response || {});
  } catch {
    // Keep previous behavior as a resilience fallback.
    yield* generateActivitySuggestionStreamAuto(conversationHistory, signal);
  }
}

const CreatePage: React.FC = () => {
  usePageTracking("Create", "CreatePage");
  const navigate = useNavigate();
  const location = useLocation();
  const { activities } = useActivityFeedData();
  const { prefetchedIdeas } = useActivityRecommendationsData();
  const {
    createActivity,
    refreshActivities,
    queueUserAction,
    inviteUsersToActivity,
  } = useActivityActions();
  const { currentUser, getUsersByIds, searchUsers } = useAuth();
  const { addToast } = useToast();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get user's activity history for personalized search
  const userActivityHistory = useMemo(() => {
    if (!currentUser) return [];
    return activities.filter(
      (a) =>
        a.creator.id === currentUser.id ||
        a.participants.some((p) => p.id === currentUser.id),
    );
  }, [activities, currentUser]);

  const timeoutRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasInitialized = useRef(false);

  const [conversation, setConversation] = useState<AIMessage[]>([]);

  const [proposalId, setProposalId] = useState<string | null>(null);
  const [proposalInvitees, setProposalInvitees] = useState<string[]>([]);
  const [proposalInviteeUsers, setProposalInviteeUsers] = useState<User[]>([]);
  const [proposalDraft, setProposalDraft] = useState<ParsedAIResponse | null>(
    null,
  );
  const [isCreatingFromProposal, setIsCreatingFromProposal] = useState(false);
  const proposalAppliedRef = useRef(false);
  const [showManageInviteesModal, setShowManageInviteesModal] = useState(false);
  const [inviteeSearchQuery, setInviteeSearchQuery] = useState("");
  const [inviteeSearchResults, setInviteeSearchResults] = useState<User[]>([]);
  const [inviteeSearchIsLoading, setInviteeSearchIsLoading] = useState(false);
  const [inviteeSearchIsLoadingMore, setInviteeSearchIsLoadingMore] =
    useState(false);
  const [inviteeSearchHasMore, setInviteeSearchHasMore] = useState(true);
  const [inviteeSearchLastDoc, setInviteeSearchLastDoc] = useState<any>(null);
  const [inviteeSearchTimeoutId, setInviteeSearchTimeoutId] =
    useState<ReturnType<typeof setTimeout> | null>(null);

  const proposalInviteeLabel = useMemo(() => {
    if (proposalInviteeUsers.length > 0) {
      return proposalInviteeUsers.map((u) => u.username).join(", ");
    }
    if (proposalInvitees.length > 0) {
      return `${proposalInvitees.length} people`;
    }
    return "friends nearby";
  }, [proposalInviteeUsers, proposalInvitees]);

  // Function to remove an invitee
  const handleRemoveInvitee = useCallback((userId: string) => {
    setProposalInvitees((prev) => prev.filter((id) => id !== userId));
    setProposalInviteeUsers((prev) =>
      prev.filter((user) => user.id !== userId),
    );
  }, []);

  // Function to add an invitee
  const handleAddInvitee = useCallback(
    (userId: string) => {
      if (!proposalInvitees.includes(userId)) {
        setProposalInvitees((prev) => [...prev, userId]);
        // The user data will be fetched automatically by existing useEffect
        setInviteeSearchResults((prev) =>
          prev.filter((user) => user.id !== userId),
        );
      }
    },
    [proposalInvitees],
  );

  const resetInviteeSearchState = useCallback(() => {
    setInviteeSearchQuery("");
    setInviteeSearchResults([]);
    setInviteeSearchLastDoc(null);
    setInviteeSearchHasMore(true);
    setInviteeSearchIsLoading(false);
    setInviteeSearchIsLoadingMore(false);
  }, []);

  const filterInviteeSearchUsers = useCallback(
    (users: User[]) => {
      return users.filter(
        (user) =>
          user.id !== currentUser?.id && !proposalInvitees.includes(user.id),
      );
    },
    [currentUser?.id, proposalInvitees],
  );

  const fetchInviteeSearch = useCallback(
    async (query: string, reset: boolean, lastDoc?: any) => {
      if (!currentUser || !searchUsers) return;

      if (reset) {
        setInviteeSearchIsLoading(true);
        setInviteeSearchLastDoc(null);
        setInviteeSearchHasMore(true);
      } else {
        setInviteeSearchIsLoadingMore(true);
      }

      try {
        const result = await searchUsers(query, reset ? null : lastDoc, 20);
        const filteredUsers = filterInviteeSearchUsers(result.users);
        if (reset) {
          setInviteeSearchResults(filteredUsers);
        } else {
          setInviteeSearchResults((prev) => {
            const existingIds = new Set(prev.map((user) => user.id));
            const newUsers = filteredUsers.filter(
              (user) => !existingIds.has(user.id),
            );
            return [...prev, ...newUsers];
          });
        }
        setInviteeSearchLastDoc(result.lastDoc);
        setInviteeSearchHasMore(result.hasMore);
      } catch (error) {
        crashlytics.logError(
          `Error searching invitees: ${getErrorMessage(error)}`,
          "CreatePage.tsx",
          error,
        );
        if (reset) {
          setInviteeSearchResults([]);
          setInviteeSearchHasMore(false);
        }
      } finally {
        setInviteeSearchIsLoading(false);
        setInviteeSearchIsLoadingMore(false);
      }
    },
    [currentUser, searchUsers, filterInviteeSearchUsers],
  );

  useEffect(() => {
    if (!showManageInviteesModal) return;

    if (inviteeSearchTimeoutId) {
      clearTimeout(inviteeSearchTimeoutId);
    }

    const timeoutId = setTimeout(() => {
      fetchInviteeSearch(inviteeSearchQuery.trim(), true, null);
    }, 300);

    setInviteeSearchTimeoutId(timeoutId);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [inviteeSearchQuery, showManageInviteesModal, fetchInviteeSearch]);

  useEffect(() => {
    if (!showManageInviteesModal) {
      resetInviteeSearchState();
    }
  }, [showManageInviteesModal, resetInviteeSearchState]);

  const loadMoreInviteeSearch = useCallback(() => {
    if (
      !showManageInviteesModal ||
      inviteeSearchIsLoadingMore ||
      inviteeSearchIsLoading ||
      !inviteeSearchHasMore ||
      !inviteeSearchLastDoc
    )
      return;
    fetchInviteeSearch(inviteeSearchQuery.trim(), false, inviteeSearchLastDoc);
  }, [
    showManageInviteesModal,
    inviteeSearchIsLoadingMore,
    inviteeSearchIsLoading,
    inviteeSearchHasMore,
    inviteeSearchLastDoc,
    fetchInviteeSearch,
    inviteeSearchQuery,
  ]);

  // Search for users to add as invitees
  const handleSearchInvitees = useCallback((query: string) => {
    setInviteeSearchQuery(query);
  }, []);

  // Track manually edited fields at the conversation level
  const [globalManuallyEditedFields, setGlobalManuallyEditedFields] = useState<
    Set<string>
  >(new Set());

  const addToGlobalManualEdits = useCallback((fieldName: string) => {
    setGlobalManuallyEditedFields((prev) => new Set([...prev, fieldName]));
  }, []);

  // Clear manual edit tracking for specific fields when user explicitly requests changes
  const clearManualEditForFields = useCallback(
    (fieldsToAllow: { [key: string]: boolean }) => {
      setGlobalManuallyEditedFields((prev) => {
        const newSet = new Set(prev);
        Object.entries(fieldsToAllow).forEach(([field, shouldAllow]) => {
          if (shouldAllow) {
            newSet.delete(field);
          }
        });
        return newSet;
      });
    },
    [],
  );
  const [userInput, setUserInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedReplies, setSuggestedReplies] = useState<string[]>([]);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastMessageTime, setLastMessageTime] = useState(0);

  // Search modal state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    // Initialize from current user's Firestore document
    return currentUser?.searchHistory || [];
  });
  const [searchResult, setSearchResult] = useState<SearchAIResponse | null>(
    null,
  );
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Keyboard height state for native platforms
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Sync search history with Firestore when user changes
  useEffect(() => {
    if (currentUser?.searchHistory) {
      setSearchHistory(currentUser.searchHistory);
    }
  }, [currentUser?.searchHistory]);
  // Detect if running on native platform
  const isNative = Capacitor.isNativePlatform();
  const isAndroid = Capacitor.getPlatform() === "android";
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(isNative ? 60 : 90);
  const [footerHeight, setFooterHeight] = useState(isNative ? 54 : 54);
  const [navHeight, setNavHeight] = useState(67); // Bottom navigation height (h-16 = 64px, but account for slight variations)
  const [heightsInitialized, setHeightsInitialized] = useState(false);

  const scrollButtonTouchStartTime = useRef<number>(0);
  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchModalKeyboardHeight, setSearchModalKeyboardHeight] = useState(0);

  const [isFocused, setIsFocused] = useState(false);
  const [isEditingInline, setIsEditingInline] = useState(false);
  const [prevIsEditingInline, setPrevIsEditingInline] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [showIdeasHint, setShowIdeasHint] = useState(false);

  // Show hint on mount
  useEffect(() => {
    // Show hint after a short delay
    const timer = setTimeout(() => {
      setShowIdeasHint(true);
    }, 1000);

    // Auto hide after 3 seconds
    const hideTimer = setTimeout(() => {
      setShowIdeasHint(false);
    }, 4000);

    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, []);

  // Focus search input when modal opens WITHOUT prefetched ideas (so user can search while waiting)
  useEffect(() => {
    if (showSearchModal) {
      // Only focus if prefetched ideas haven't loaded yet
      const shouldFocus = !prefetchedIdeas?.ideas?.length;
      if (shouldFocus) {
        // Delay to ensure modal animation and input render completes
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 300); // Increased delay to 300ms
      }
    }
  }, [showSearchModal]); // Only depend on showSearchModal to trigger on every open

  // Handle keyboard for search modal on iOS
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== "ios")
      return;
    if (!showSearchModal) {
      setSearchModalKeyboardHeight(0);
      return;
    }

    const cb = (isOpen: boolean, height?: number) => {
      setSearchModalKeyboardHeight(isOpen ? (height ?? 0) : 0);
    };

    KeyboardHandler.getInstance().initialize();
    KeyboardHandler.getInstance().onKeyboardChange(cb);

    return () => {
      KeyboardHandler.getInstance().offKeyboardChange(cb);
      setSearchModalKeyboardHeight(0);
    };
  }, [showSearchModal]);

  // Handle scroll to load more messages and show/hide scroll-to-bottom button
  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Check if user has scrolled up from bottom
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom < 100;
    // Show button if not near bottom
    if (!isNearBottom && !showNewMessageIndicator) {
      setShowNewMessageIndicator(true);
    } else if (isNearBottom && showNewMessageIndicator) {
      setShowNewMessageIndicator(false);
    }
  };

  const allowNavigationRef = useRef(false);
  const navAfterReturnFromManual = useRef(false);
  const [manualBlockMessage, setManualBlockMessage] = useState<string | null>(
    null,
  );
  const [resetClicked, setResetClicked] = useState(false);

  // Track if user has progress (more than initial message or editing inline or has been editing manually)
  const hasProgress = useMemo(() => {
    return (
      conversation.length > 1 ||
      (conversation.length === 1 && conversation[0].role === "user") ||
      isEditingInline ||
      navAfterReturnFromManual.current ||
      resetClicked
    );
  }, [
    conversation,
    isEditingInline,
    navAfterReturnFromManual.current,
    resetClicked,
  ]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (allowNavigationRef.current) {
      allowNavigationRef.current = false;
      return false; // allow navigation - do not show blocker
    }
    if (!hasProgress) {
      return false; // no progress, allow navigation - do not show blocker
    }
    // same page is refreshed on reset or after coming from manual
    if (currentLocation.pathname === nextLocation.pathname) {
      setResetClicked(false);
      return false;
    }
    return true; // show blocker
  });

  // Warn on browser refresh/close if there is progress
  useEffect(() => {
    if (!hasProgress) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (allowNavigationRef.current) {
        allowNavigationRef.current = false;
        return;
      }
      e.preventDefault();
      e.returnValue = ""; // Required by some browsers to trigger the dialog
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasProgress]);

  const refreshCreatePage = () => {
    // Clear manual navigation flag
    navAfterReturnFromManual.current = false;

    // Clear session storage
    sessionStorage.removeItem("createPageConversation");
    sessionStorage.removeItem("createPageSuggestedReplies");

    // Reset to initial state
    const initialMessage: AIMessage = {
      role: "model",
      content: {
        followUpQuestions: [
          "Hi! I'm here to help you create amazing activities. What sounds fun to you today? 🌟",
        ],
        suggestedReplies: [
          "Movies 🎬",
          "Coffee & chat ☕",
          "Outdoor adventure 🏞️",
          "Food & drinks 🍽️",
          "Surprise me! ✨",
        ],
      },
      timestamp: generateUniqueTimestamp(),
    };

    setConversation([initialMessage]);
    if (isParsedAIResponse(initialMessage.content)) {
      setSuggestedReplies(initialMessage.content.suggestedReplies || []);
    }

    // Clear any manual edit tracking
    setGlobalManuallyEditedFields(new Set());

    // Show success toast
    navigate("/create", { replace: true });
  };

  const handleDiscard = () => {
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
    if (resetClicked) {
      setResetClicked(false);
      refreshCreatePage();
    }
  };

  const handleCancel = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setResetClicked(false);
  };

  useEffect(() => {
    if (isEditingInline && isEditingInline !== prevIsEditingInline) {
      setPrevIsEditingInline(isEditingInline);
    }
  }, [isEditingInline, prevIsEditingInline]);

  // Attach scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [showNewMessageIndicator, messagesContainerRef]);

  // Scroll to bottom - wait for layout then scroll once
  const scrollToBottomWithContainer = useCallback((onComplete?: () => void) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Wait for layout to be ready, then scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
          onComplete?.();
        }
      });
    });
  }, []);

  // 1. Attach listeners to the input
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    input.addEventListener("focus", handleFocus);
    input.addEventListener("blur", handleBlur);

    return () => {
      input.removeEventListener("focus", handleFocus);
      input.removeEventListener("blur", handleBlur);
    };
  }, []);

  // 2. Trigger scroll logic only when isFocused changes
  useEffect(() => {
    // do not scroll if editing inline
    if (isEditingInline) {
      return;
    } else {
      // now it is not editing inline
      if (prevIsEditingInline) {
        // just exited inline editing, do not scroll
        setPrevIsEditingInline(false);
        return;
      }
    }
    let attempts = 0;
    const maxAttempts = 3;
    const tryScroll = (isAtBottomFromContainer?: boolean) => {
      const footer = footerRef.current;
      if (!footer) return;
      // 1. Capture the "Current" value NOW
      const initialHeight = footer.clientHeight;
      setTimeout(() => {
        // 2. Capture the "New" value after 100ms
        const newHeight = footer.clientHeight;
        if (newHeight !== initialHeight || !isAtBottomFromContainer) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              attempts++;
              scrollToBottomWithContainer(() => {
                const container = messagesContainerRef.current;
                if (!container) return;
                const distanceFromBottom =
                  container.scrollHeight -
                  container.scrollTop -
                  container.clientHeight;
                const isAtBottom = distanceFromBottom < 1;
                if (
                  !(isAtBottom || isAtBottomFromContainer) &&
                  attempts < maxAttempts
                ) {
                  setTimeout(tryScroll, 100);
                }
              });
            });
          });
        }
      }, 100);
    };
    tryScroll();

    const container = messagesContainerRef.current;
    if (!container) return;
    // 1. Create the observer
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        // entry.contentRect or entry.target.clientHeight
        // will give you the most up-to-date dimensions
        const d1 =
          container.scrollHeight - container.scrollTop - container.clientHeight;
        const d2 =
          container.scrollHeight -
          container.scrollTop -
          entry.target.clientHeight;
        const i1 = d1 < 1;
        const i2 = d2 < 1;
        tryScroll(i1 && i2);
      }
    });
    // 3. Start observing the container
    resizeObserver.observe(container);
    // 4. Cleanup on unmount or when dependencies change
    return () => resizeObserver.disconnect();
  }, [
    isFocused,
    scrollToBottomWithContainer,
    isLoading,
    isEditingInline,
    keyboardHeight,
    navHeight,
    messagesContainerRef.current,
  ]);

  // Handle keyboard show/hide on native platforms
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const cb = (isOpen: boolean, height?: number) => {
      setKeyboardHeight(isOpen ? (height ?? 0) : 0);
    };
    KeyboardHandler.getInstance().initialize();
    KeyboardHandler.getInstance().onKeyboardChange(cb);
    return () => {
      KeyboardHandler.getInstance().offKeyboardChange(cb);
    };
  }, []);

  // Calculate heights once after initial render
  useEffect(() => {
    const updateHeights = () => {
      // Single measurement pass
      const header = headerRef.current;
      const footer = footerRef.current;
      const nav = document.querySelector("footer.z-50") as HTMLElement;

      if (!header || !footer) {
        // Retry once if elements aren't ready
        setTimeout(updateHeights, 100);
        return;
      }

      // Read all heights in one batch
      const measuredHeaderHeight = header.clientHeight;
      const footerHeightValue = footer.clientHeight;
      const navHeightValue = nav ? nav.clientHeight : 0;

      // Add status banner height if present
      let totalHeaderHeight = measuredHeaderHeight;

      // Always update on first initialization, then only if changed significantly
      if (!heightsInitialized) {
        setHeaderHeight(totalHeaderHeight);
        setFooterHeight(footerHeightValue);
        setNavHeight(navHeightValue);
        setHeightsInitialized(true);
      } else {
        // Update state only if changed significantly (avoid unnecessary rerenders)
        if (Math.abs(totalHeaderHeight - headerHeight) > 2) {
          setHeaderHeight(totalHeaderHeight);
        }
        if (Math.abs(footerHeightValue - footerHeight) > 2) {
          setFooterHeight(footerHeightValue);
        }
        if (Math.abs(navHeightValue - navHeight) > 2) {
          setNavHeight(navHeightValue);
        }
      }
    };

    // For iOS, use multiple attempts with increasing delays
    const timer = setTimeout(updateHeights, 50);

    // Also observe footer height changes
    let footerResizeObserver = null;
    if (footerRef.current) {
      footerResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(updateHeights);
      });
      footerResizeObserver.observe(footerRef.current);
    }

    // Also observer messagesContainerRef height changes (e.g. keyboard open/close)
    const chatContainer = messagesContainerRef.current;
    let messagesContainerResizeObserver = null;
    if (chatContainer) {
      messagesContainerResizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const d1 =
            chatContainer.scrollHeight -
            chatContainer.scrollTop -
            chatContainer.clientHeight;
          const d2 =
            chatContainer.scrollHeight -
            chatContainer.scrollTop -
            entry.target.clientHeight;
          const approach1AtBottom = d1 < 1;
          const approach2AtBottom = d2 < 1;
          if (!approach1AtBottom || !approach2AtBottom) {
            requestAnimationFrame(updateHeights);
          }
        }
      });
      messagesContainerResizeObserver.observe(chatContainer);
    }

    return () => {
      clearTimeout(timer);
      footerResizeObserver && footerResizeObserver.disconnect();
      messagesContainerResizeObserver &&
        messagesContainerResizeObserver.disconnect();
    };
  }, [
    headerHeight,
    footerHeight,
    navHeight,
    heightsInitialized,
    keyboardHeight,
  ]);

  const handleScrollToBottom = () => {
    setShowNewMessageIndicator(false);
    scrollToBottomWithContainer();
  };

  useEffect(() => {
    loadGoogleMapsApi(["places"])
      .then(() => setIsApiLoaded(true))
      .catch((err) => {
        crashlytics.logError(
          `Error loading Google Maps API: ${JSON.stringify(err)}`,
          "CreatePage.tsx",
          err,
        );
        addToast(err.message || "Location search is unavailable.", "error");
      });
  }, [addToast]);

  // Cleanup effect to abort ongoing API calls when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // Also abort calls when location changes (user navigates away)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [location]);

  const isParsedAIResponse = (content: any): content is ParsedAIResponse => {
    return (
      typeof content === "object" &&
      content !== null &&
      ("followUpQuestions" in content || "suggestedReplies" in content)
    );
  };

  const handlePostActivity = async (activityData: ParsedAIResponse) => {
    try {
      setIsLoading(true);
      if (!currentUser) {
        addToast("Please sign in to create an activity.", "error");
        setIsLoading(false);
        return;
      }
      // Validate required fields
      if (!activityData.title?.trim()) {
        addToast("Activity title is required.", "error");
        setIsLoading(false);
        return;
      }
      if (!activityData.location?.trim()) {
        addToast("Activity location is required.", "error");
        setIsLoading(false);
        return;
      }
      if (!activityData.coordinates) {
        addToast(
          "Location must be verified. Please select a valid location from the map.",
          "error",
        );
        setIsLoading(false);
        return;
      }
      if (!activityData.suggestedDateTime) {
        addToast("Activity date and time is required.", "error");
        setIsLoading(false);
        return;
      }
      const durationMinutes = getDurationAsMinutes(activityData.duration);
      if (!durationMinutes || durationMinutes < 15) {
        addToast("Activity duration must be at least 15 minutes.", "error");
        setIsLoading(false);
        return;
      }
      const groupSizeNumber = getGroupSizeAsNumber(activityData.groupSize);
      if (!groupSizeNumber || groupSizeNumber < 2) {
        addToast("Group size must be at least 2 people.", "error");
        setIsLoading(false);
        return;
      }

      // Validate date/time is in the future
      const selectedDateTime = toJsDate(activityData.suggestedDateTime);
      const now = new Date();
      const oneMinuteFromNow = new Date(now.getTime() + 60000);

      if (selectedDateTime < oneMinuteFromNow) {
        addToast(
          "Activity must be scheduled at least 1 minute in the future.",
          "error",
        );
        setIsLoading(false);
        return;
      }

      addToast("Creating your activity...", "info");

      const newActivityId = await createActivity({
        title: activityData.title,
        description: activityData.description || null,
        location: activityData.location,
        coordinates: activityData.coordinates || null,
        dateTime: activityData.suggestedDateTime!,
        duration: durationMinutes,
        groupSize: groupSizeNumber,
        category: getValidCategory(activityData.category),
        imageUrl: activityData.imageUrl || undefined,
        creatorId: currentUser.id,
        isPrivate: activityData.isPrivate || false,
      });

      if (newActivityId) {
        setIsLoading(false);
        addToast("🎉 Activity posted successfully!", "success");
        allowNavigationRef.current = true;
        try {
          navigate(`/activity/${newActivityId}`);
        } catch (navigationError) {
          crashlytics.logError(
            `Error during navigation: ${String(navigationError)}`,
            "CreatePage.tsx",
            navigationError,
          );
          window.location.href = `/activity/${newActivityId}`;
        }

        void (async () => {
          try {
            const invitees = proposalInvitees.filter(
              (id) => id !== currentUser?.id,
            );
            const backgroundTasks: Promise<any>[] = [refreshActivities()];
            if (invitees.length > 0) {
              backgroundTasks.push(inviteUsersToActivity(newActivityId, invitees));
            }
            await Promise.all(backgroundTasks);
          } catch (backgroundError) {
            crashlytics.logError(
              `Error in post-create background sync: ${String(backgroundError)}`,
              "CreatePage.tsx",
              backgroundError,
            );
          }
        })();
      } else {
        crashlytics.logError(
          "Post Flow: Activity creation returned null/undefined",
          "CreatePage.tsx",
          new Error("Activity creation returned null/undefined"),
        );
        setIsLoading(false);
        throw new Error("Activity creation failed");
      }
    } catch (error) {
      crashlytics.logError(
        `Error posting activity: ${getErrorMessage(error)}`,
        "CreatePage.tsx",
        error,
      );
      addToast("Failed to post activity. Please try again.", "error");
      setIsLoading(false);
      throw error; // Re-throw to handle in the component
    }
  };

  const applyPrefilledData = useCallback(
    (
      prefilledData: ParsedAIResponse,
      overrides?: { followUpQuestions?: string[]; suggestedReplies?: string[] },
    ) => {
      if (!prefilledData || Object.keys(prefilledData).length === 0) return;

      // Check if we have a saved conversation from when user went to manual mode
      const savedConversation = sessionStorage.getItem(
        "createPageConversation",
      );

      if (savedConversation) {
        // Restore the previous conversation
        const restoredConversation = JSON.parse(
          savedConversation,
        ) as AIMessage[];

        allowNavigationRef.current = false;
        navAfterReturnFromManual.current = true;
        setManualBlockMessage(
          "You switched to manual mode. Are you sure you want to leave this page and lose your progress?",
        );

        // Update the last AI message with the new prefilled data from manual page
        const updatedConversation = restoredConversation.map((msg, index) => {
          if (
            index === restoredConversation.length - 1 &&
            msg.role === "model" &&
            isParsedAIResponse(msg.content)
          ) {
            return {
              ...msg,
              content: {
                ...(msg.content as ParsedAIResponse),
                ...prefilledData,
                followUpQuestions: overrides?.followUpQuestions || [
                  "Updated! Here's your activity with the changes. Ready to post?",
                ],
                suggestedReplies: overrides?.suggestedReplies || [
                  "Post it!",
                  "Make more changes",
                  "Edit details",
                ],
              },
              timestamp: generateUniqueTimestamp(),
            };
          }
          return msg;
        });

        setConversation(updatedConversation);

        // Set suggested replies
        const lastMsg = updatedConversation[updatedConversation.length - 1];
        if (isParsedAIResponse(lastMsg.content)) {
          setSuggestedReplies(lastMsg.content.suggestedReplies || []);
        }

        // Clean up sessionStorage
        sessionStorage.removeItem("createPageConversation");
        sessionStorage.removeItem("createPageSuggestedReplies");
      } else {
        // No saved conversation, create a new one with prefilled data
        const summaryMessage: AIMessage = {
          role: "model",
          content: {
            ...prefilledData,
            followUpQuestions: overrides?.followUpQuestions || [
              "Picking up where you left off! Here's the draft so far. You can ask me to make changes, or we can post it.",
            ],
            suggestedReplies: overrides?.suggestedReplies || [
              "Looks good, post it!",
              "Change the time",
              "Edit the location",
            ],
          },
          timestamp: generateUniqueTimestamp(),
        };
        setConversation([summaryMessage]);
        if (isParsedAIResponse(summaryMessage.content)) {
          setSuggestedReplies(summaryMessage.content.suggestedReplies || []);
        }
      }

      hasInitialized.current = true;
    },
    [],
  );

  useEffect(() => {
    const prefilledData = location.state?.prefilledData as
      | ParsedAIResponse
      | undefined;

    if (prefilledData && Object.keys(prefilledData).length > 0) {
      applyPrefilledData(prefilledData);

      if (Array.isArray(location.state?.proposalInvitees)) {
        setProposalInvitees(location.state.proposalInvitees as string[]);
      }
      if (location.state?.proposalId) {
        setProposalId(location.state.proposalId as string);
      }

      // Clear the state to prevent this logic from running again on refresh/re-render.
      navigate(location.pathname, { replace: true, state: {} });
    } else if (!hasInitialized.current && conversation.length === 0) {
      // Only set the initial message if the conversation is empty and we haven't initialized yet
      const initialMessage: AIMessage = {
        role: "model",
        content: {
          followUpQuestions: [
            "Hi! I'm here to help you create amazing activities. What sounds fun to you today? 🌟",
          ],
          suggestedReplies: [
            "Movies 🎬",
            "Coffee & chat ☕",
            "Outdoor adventure 🏞️",
            "Food & drinks 🍽️",
            "Surprise me! ✨",
          ],
        },
        timestamp: generateUniqueTimestamp(),
      };
      setConversation([initialMessage]);
      if (isParsedAIResponse(initialMessage.content)) {
        setSuggestedReplies(initialMessage.content.suggestedReplies || []);
      }
      hasInitialized.current = true;
    }
  }, [location.state, conversation.length, navigate, applyPrefilledData]); // Listen to location.state changes

  const proposalIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("proposalId");
  }, [location.search]);

  useEffect(() => {
    const firestore = db;
    if (
      !proposalIdFromQuery ||
      !currentUser ||
      proposalAppliedRef.current ||
      !firestore
    )
      return;

    let cancelled = false;

    const loadProposal = async () => {
      try {
        const proposalRef = doc(
          firestore!,
          "matchProposals",
          proposalIdFromQuery,
        );
        const proposalSnap = await getDoc(proposalRef);

        if (!proposalSnap.exists()) {
          addToast("This match suggestion is no longer available.", "info");
          return;
        }

        const proposalData = proposalSnap.data() as any;
        if (proposalData.ownerUserId !== currentUser.id) {
          addToast(
            "This match suggestion does not belong to your account.",
            "error",
          );
          return;
        }

        if (["declined", "expired"].includes(proposalData.status)) {
          addToast("This match suggestion has expired.", "info");
          return;
        }

        const draft = proposalData.activityDraft || {};
        if (!cancelled) {
          applyPrefilledData(draft as ParsedAIResponse, {
            followUpQuestions: [
              "Here's a curated match suggestion. Want to post it as-is or tweak the details?",
            ],
            suggestedReplies: [
              "Looks good, post it!",
              "Change the time",
              "Edit the location",
            ],
          });
          setProposalId(proposalSnap.id);
          setProposalInvitees(
            Array.isArray(proposalData.candidateUserIds)
              ? proposalData.candidateUserIds
              : [],
          );
          setProposalDraft(draft as ParsedAIResponse);
          proposalAppliedRef.current = true;
        }
      } catch (error) {
        crashlytics.logError(
          `Error loading match proposal: ${getErrorMessage(error)}`,
          "CreatePage.tsx",
          error,
        );
        addToast(
          "Unable to load the match suggestion. Please try again.",
          "error",
        );
      }
    };

    loadProposal();

    return () => {
      cancelled = true;
    };
  }, [proposalIdFromQuery, currentUser, applyPrefilledData, addToast]);

  useEffect(() => {
    let cancelled = false;

    if (!proposalInvitees.length || !getUsersByIds) {
      setProposalInviteeUsers([]);
      return undefined;
    }

    const loadInvitees = async () => {
      try {
        const users = await getUsersByIds(proposalInvitees);
        if (!cancelled) {
          setProposalInviteeUsers(users);
        }
      } catch (error) {
        crashlytics.logError(
          `Error loading proposal invitees: ${getErrorMessage(error)}`,
          "CreatePage.tsx",
          error,
        );
      }
    };

    loadInvitees();

    return () => {
      cancelled = true;
    };
  }, [proposalInvitees, getUsersByIds]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  };

  const handleCreateFromProposal = useCallback(async () => {
    if (!currentUser) return;

    const latestAiMessage = [...conversation]
      .reverse()
      .find((msg) => msg.role === "model" && isParsedAIResponse(msg.content));
    const draft =
      (latestAiMessage?.content as ParsedAIResponse) || proposalDraft;

    if (
      !draft ||
      !draft.title ||
      !draft.location ||
      !draft.suggestedDateTime ||
      !draft.duration ||
      !draft.groupSize ||
      !draft.category ||
      !draft.coordinates
    ) {
      addToast(
        "This suggestion needs more details. Please tweak the draft before posting.",
        "info",
      );
      return;
    }

    setIsCreatingFromProposal(true);
    try {
      const newActivityId = await createActivity({
        title: draft.title,
        description: draft.description || null,
        location: draft.location,
        coordinates: draft.coordinates,
        dateTime: draft.suggestedDateTime,
        duration:
          typeof draft.duration === "string"
            ? getDurationAsMinutes(draft.duration)
            : draft.duration,
        groupSize: draft.groupSize,
        category: getValidCategory(draft.category),
        creatorId: currentUser.id,
        isPrivate: draft.isPrivate ?? true,
      });

      if (!newActivityId) {
        throw new Error("Activity creation failed");
      }
      allowNavigationRef.current = true;
      navigate(`/activity/${newActivityId}`, { replace: true });

      void (async () => {
        try {
          const invitees = proposalInvitees.filter((id) => id !== currentUser.id);
          const backgroundTasks: Promise<any>[] = [refreshActivities()];

          if (invitees.length > 0) {
            backgroundTasks.push(inviteUsersToActivity(newActivityId, invitees));
          }

          if (proposalId && db) {
            backgroundTasks.push(
              updateDoc(doc(db, "matchProposals", proposalId), {
                status: "created",
                createdActivityId: newActivityId,
                createdAt: serverTimestamp(),
              }),
            );
          }

          await Promise.all(backgroundTasks);
        } catch (backgroundError) {
          crashlytics.logError(
            `Error in proposal post-create background sync: ${String(backgroundError)}`,
            "CreatePage.tsx",
            backgroundError,
          );
        }
      })();
    } catch (error) {
      crashlytics.logError(
        `Error creating from match proposal: ${getErrorMessage(error)}`,
        "CreatePage.tsx",
        error,
      );
      addToast("Failed to create the activity. Please try again.", "error");
    } finally {
      setIsCreatingFromProposal(false);
    }
  }, [
    proposalDraft,
    proposalInvitees,
    proposalId,
    currentUser,
    conversation,
    createActivity,
    inviteUsersToActivity,
    refreshActivities,
    navigate,
    addToast,
  ]);

  useEffect(scrollToBottom, [conversation, suggestedReplies]);

  const handleNavigateToManual = useCallback(() => {
    const lastAiMessage = [...conversation]
      .reverse()
      .find((msg) => msg.role === "model" && isParsedAIResponse(msg.content));

    const initialData = lastAiMessage
      ? (lastAiMessage.content as ParsedAIResponse)
      : null;

    // Store the full conversation in sessionStorage to restore it when coming back
    sessionStorage.setItem(
      "createPageConversation",
      JSON.stringify(conversation),
    );
    sessionStorage.setItem(
      "createPageSuggestedReplies",
      JSON.stringify(suggestedReplies),
    );

    allowNavigationRef.current = true;
    navigate("/create/manual", { state: { initialData, proposalInvitees } });
  }, [conversation, suggestedReplies, proposalInvitees, navigate]);

  const handleResetActivity = useCallback(() => {
    if (hasProgress) {
      setResetClicked(true);
      if (!manualBlockMessage) {
        setManualBlockMessage(
          "Are you sure you want to start over? You will lose the current activity you are creating.",
        );
      }
    } else {
      refreshCreatePage();
    }
  }, [conversation, addToast, manualBlockMessage, hasProgress]);

  const handleUpdateAIMessage = useCallback(
    async (timestamp: string, newData: ParsedAIResponse) => {
      // For manual edits (when user directly edits the form), don't apply complex validation
      // Just update the conversation directly since the form data is already validated
      navAfterReturnFromManual.current = true;
      setConversation((prev) => {
        const updated = prev.map((msg) => {
          if (msg.timestamp === timestamp) {
            // Create completely new message object to ensure React detects the change
            const newMessage = {
              ...msg,
              content: JSON.parse(JSON.stringify(newData)), // Deep clone to ensure new reference
              timestamp: timestamp,
            };
            return newMessage;
          } else {
            return msg;
          }
        });

        return updated;
      });
    },
    [],
  );

  // Check if user is explicitly requesting changes to specific fields
  const isUserRequestingFieldChanges = (
    userMessage: string,
  ): { [key: string]: boolean } => {
    const message = userMessage.toLowerCase();

    return {
      title:
        /change.*title|update.*title|new title|different title|rename|call it|title.*should be|make.*title/i.test(
          message,
        ),
      description:
        /change.*description|update.*description|make it.*about|describe.*differently|description.*should|add.*details|add.*that|add.*info|include.*that|mention.*that|note.*that/i.test(
          message,
        ),
      location:
        /change.*location|update.*location|move.*to|different.*place|somewhere else|location.*should|at.*[a-z]+ park|in.*[a-z]+ city/i.test(
          message,
        ),
      dateTime:
        /change.*time|update.*time|different.*time|reschedule|move.*to.*[0-9]|at [0-9]|tomorrow|today|next week|this weekend|evening|morning|afternoon/i.test(
          message,
        ),
      duration:
        /change.*duration|update.*duration|make it.*longer|make it.*shorter|[0-9].*hour|[0-9].*minute|longer|shorter|quick|brief/i.test(
          message,
        ),
      groupSize:
        /change.*group|update.*group|more people|fewer people|[0-9].*people|bigger group|smaller group|just.*us|invite.*more/i.test(
          message,
        ),
      category:
        /change.*category|update.*category|different.*type|make it.*fitness|make it.*dining|make it.*movies|make it.*outdoor|change.*to.*[a-z]+|this.*is.*[a-z]+.*activity|category.*should|type.*should/i.test(
          message,
        ),
    };
  };

  // Function to validate and enhance AI-provided locations
  const validateAndEnhanceLocation = async (
    data: ParsedAIResponse,
  ): Promise<ParsedAIResponse> => {
    // If location is provided by AI but doesn't have placeId, try to validate it
    if (data.location && !data.placeId) {
      try {
        // Try to geocode the location to get valid Google Maps data
        const geocodeResult = await geocodeByAddress(
          data.location + ", Singapore",
        );
        if (
          geocodeResult &&
          geocodeResult.isValid &&
          geocodeResult.formattedAddress &&
          geocodeResult.coordinates
        ) {
          return {
            ...data,
            location: geocodeResult.formattedAddress,
            coordinates: geocodeResult.coordinates,
          };
        }
      } catch (error) {
        // If validation fails, keep the original location but mark it as unvalidated
        // The user will need to manually select a proper location
      }
    }

    return data;
  };

  // Function to intelligently refine the activity title based on progression
  const refineActivityTitle = (data: ParsedAIResponse): ParsedAIResponse => {
    // First, check if the current title is just conversational AI response that shouldn't be a title
    if (data.title && isConversationalResponse(data.title)) {
      // Move conversational content to description and create proper title
      const existingDescription = data.description || "";
      const newDescription = existingDescription
        ? `${data.title}\n\n${existingDescription}`
        : data.title;

      return {
        ...data,
        title: generateProgressiveTitle(data),
        description: refineProgressiveDescription(data, newDescription),
      };
    }

    // If title is already good and specific, enhance it progressively
    if (
      data.title &&
      data.title.length > 15 &&
      data.title.length <= 50 &&
      !isGenericTitle(data.title)
    ) {
      return {
        ...data,
        title: enhanceExistingTitle(data),
        description: refineProgressiveDescription(data, data.description),
      };
    }

    // Generate a structured title based on actual activity data
    const refinedTitle = generateProgressiveTitle(data);

    return {
      ...data,
      title: refinedTitle,
      description: refineProgressiveDescription(data, data.description),
    };
  };

  // Enhanced category detection and refinement
  const enhanceActivityCategory = (
    data: ParsedAIResponse,
  ): ParsedAIResponse => {
    // Detect the best category based on title, description, and location
    const detectedCategory = detectActivityCategory(
      data.title || "",
      data.description || "",
      data.location || "",
    );

    // Get confidence level for the detection
    const confidence = getCategoryConfidence(
      data.title || "",
      data.description || "",
      data.location || "",
      detectedCategory,
    );

    // If we have high confidence in the detection, or if no category was set
    if (confidence > 60 || !data.category) {
      return {
        ...data,
        category: detectedCategory,
      };
    }

    // If the current category has very low confidence, suggest the detected one
    if (data.category) {
      const currentConfidence = getCategoryConfidence(
        data.title || "",
        data.description || "",
        data.location || "",
        getValidCategory(data.category) as ActivityCategory,
      );

      if (confidence > currentConfidence + 20) {
        // Significant improvement
        return {
          ...data,
          category: detectedCategory,
        };
      }
    }

    // Keep the existing category if it's reasonable
    return data;
  };

  // Progressive title generation that improves with more context
  const generateProgressiveTitle = (data: ParsedAIResponse): string => {
    const components = [];

    // Extract activity type from category or description with more sophistication
    let activityType = "";
    if (data.category) {
      const categoryMap: { [key: string]: string } = {
        dining: "Dining Experience",
        outdoor: "Outdoor Adventure",
        movies: "Movie Night",
        events: "Event Experience",
        sports: "Sports Activity",
        culture: "Cultural Experience",
        nightlife: "Nightlife Experience",
        wellness: "Wellness Activity",
      };
      activityType = categoryMap[data.category.toLowerCase()] || data.category;
    }

    // Extract more specific activity details from description
    if (data.description && activityType) {
      const specificActivities = extractSpecificActivity(
        data.description,
        data.category,
      );
      if (specificActivities) {
        activityType = specificActivities;
      }
    }

    // Add time context for better specificity
    if (data.suggestedDateTime) {
      const dateTime = toJsDate(data.suggestedDateTime);
      const hour = dateTime.getHours();
      if (hour >= 6 && hour < 12) {
        activityType = `Morning ${activityType}`;
      } else if (hour >= 12 && hour < 17) {
        activityType = `Afternoon ${activityType}`;
      } else if (hour >= 17 && hour < 21) {
        activityType = `Evening ${activityType}`;
      } else if (hour >= 21 || hour < 6) {
        activityType = `Night ${activityType}`;
      }
    }

    if (activityType) {
      components.push(activityType);
    }

    // Add location context with neighborhood/area intelligence
    if (data.location) {
      const locationInfo = extractLocationContext(data.location);
      if (locationInfo) {
        components.push(locationInfo);
      }
    }

    // Add group size context for social activities
    if (data.groupSize && data.groupSize > 2) {
      if (data.groupSize <= 4) {
        components.push("(Small Group)");
      } else if (data.groupSize <= 8) {
        components.push("(Group Activity)");
      } else {
        components.push("(Large Group)");
      }
    }

    // Combine components into a progressive title
    let title = components.join(" ");

    // Fallback if no components
    if (!title || title.length < 5) {
      title = "Social Activity Plan";
    }

    // Ensure title isn't too long
    if (title.length > 50) {
      title = title.substring(0, 47) + "...";
    }

    return title;
  };

  // Enhance existing good titles with more context
  const enhanceExistingTitle = (data: ParsedAIResponse): string => {
    let title = data.title || "";

    // Add time context if not already present
    if (
      data.suggestedDateTime &&
      !title.match(/morning|afternoon|evening|night/i)
    ) {
      const dateTime = toJsDate(data.suggestedDateTime);
      const hour = dateTime.getHours();
      if (hour >= 6 && hour < 12 && !title.includes("Morning")) {
        title = `Morning ${title}`;
      } else if (hour >= 17 && hour < 21 && !title.includes("Evening")) {
        title = `Evening ${title}`;
      }
    }

    // Add location context if specific and not too long
    if (
      data.location &&
      !title.toLowerCase().includes(data.location.toLowerCase().split(",")[0])
    ) {
      const locationParts = data.location.split(",");
      const mainLocation = locationParts[0]?.trim();
      if (
        mainLocation &&
        mainLocation.length < 20 &&
        title.length + mainLocation.length < 45
      ) {
        title = `${title} at ${mainLocation}`;
      }
    }

    return title.length > 50 ? title.substring(0, 47) + "..." : title;
  };

  // Progressive description refinement
  const refineProgressiveDescription = (
    data: ParsedAIResponse,
    currentDescription?: string,
  ): string => {
    // Try to get description from data if currentDescription is empty but data has it
    const actualDescription = currentDescription || data.description;

    // First, always check if the description contains important information (cost, requirements, etc.)
    if (actualDescription && actualDescription.length > 0) {
      const hasImportantInfo =
        /(\$|cost|charge|fee|price|bring|required|skill|level|beginner|advanced|equipment)/i.test(
          actualDescription,
        );

      if (hasImportantInfo) {
        // Preserve the full AI description when it contains important details - ALWAYS preserve cost information
        return actualDescription.length > 500
          ? actualDescription.substring(0, 497) + "..."
          : actualDescription;
      }
    }

    // If we have a good AI-provided description, preserve it as-is
    if (
      actualDescription &&
      actualDescription.length > 20 &&
      !isConversationalResponse(actualDescription)
    ) {
      return actualDescription.length > 500
        ? actualDescription.substring(0, 497) + "..."
        : actualDescription;
    }

    const descriptionParts = [];

    // Start with existing description if good (only if we didn't already return above for important info)
    if (
      actualDescription &&
      actualDescription.length > 20 &&
      !isConversationalResponse(actualDescription)
    ) {
      descriptionParts.push(actualDescription);
    }

    // Add contextual details based on available information (only if no description exists)
    if (descriptionParts.length === 0) {
      const contextualDetails = generateContextualDetails(data);
      if (contextualDetails.length > 0) {
        descriptionParts.push(...contextualDetails);
      }
    }

    const finalDescription = descriptionParts.join(" ").trim();

    // Ensure description isn't too long
    return finalDescription.length > 500
      ? finalDescription.substring(0, 497) + "..."
      : finalDescription;
  };

  // Generate contextual details based on activity data
  const generateContextualDetails = (data: ParsedAIResponse): string[] => {
    const details = [];

    // Add timing context
    if (data.suggestedDateTime) {
      const dateTime = toJsDate(data.suggestedDateTime);
      const dayOfWeek = dateTime.toLocaleDateString("en-US", {
        weekday: "long",
      });
      const isWeekend = dayOfWeek === "Saturday" || dayOfWeek === "Sunday";

      if (isWeekend) {
        details.push("Perfect weekend activity to unwind and socialize.");
      } else {
        details.push("Great weekday activity to break from routine.");
      }
    }

    // Add duration context
    if (data.duration) {
      const durationMinutes =
        typeof data.duration === "string"
          ? getDurationAsMinutes(data.duration)
          : data.duration;
      if (durationMinutes <= 60) {
        details.push("Quick and enjoyable experience.");
      } else if (durationMinutes <= 180) {
        details.push("Relaxed paced activity with time to connect.");
      } else {
        details.push("Extended experience for deeper engagement.");
      }
    }

    // Add group dynamics context
    if (data.groupSize) {
      if (data.groupSize === 2) {
        details.push("Intimate setting for meaningful conversation.");
      } else if (data.groupSize <= 4) {
        details.push("Small group perfect for close interaction.");
      } else {
        details.push("Social gathering with diverse perspectives.");
      }
    }

    // Add category-specific context
    if (data.category) {
      const categoryContext = getCategorySpecificContext(
        data.category,
        data.location,
      );
      if (categoryContext) {
        details.push(categoryContext);
      }
    }

    return details;
  };

  // Extract location context with Singapore-specific knowledge
  const extractLocationContext = (location: string): string | null => {
    const locationLower = location.toLowerCase();

    // Singapore-specific areas
    if (locationLower.includes("orchard")) return "in Orchard";
    if (locationLower.includes("marina bay")) return "at Marina Bay";
    if (locationLower.includes("sentosa")) return "on Sentosa";
    if (locationLower.includes("clarke quay")) return "at Clarke Quay";
    if (locationLower.includes("chinatown")) return "in Chinatown";
    if (locationLower.includes("little india")) return "in Little India";
    if (locationLower.includes("east coast")) return "at East Coast";
    if (locationLower.includes("botanic")) return "at Botanic Gardens";

    // Generic extraction
    const locationParts = location.split(",");
    const mainLocation = locationParts[0]?.trim();
    if (mainLocation && mainLocation.length < 25) {
      return `at ${mainLocation}`;
    }

    return null;
  };

  // Get category-specific contextual information
  const getCategorySpecificContext = (
    category: string,
    location?: string,
  ): string | null => {
    switch (category.toLowerCase()) {
      case "dining":
        return "Discover new flavors and enjoy good company over a meal.";
      case "outdoor":
        return "Fresh air and physical activity in a beautiful setting.";
      case "movies":
        return "Shared entertainment experience with discussion opportunities.";
      case "culture":
        return "Explore heritage and arts while learning together.";
      case "sports":
        return "Active fun with friendly competition and teamwork.";
      case "events":
        return "Special occasion with unique experiences to remember.";
      default:
        return null;
    }
  };

  // Extract specific activity mentions from description
  const extractSpecificActivity = (
    description: string,
    category?: string,
  ): string | null => {
    if (!description) return null;

    const lowerDesc = description.toLowerCase();

    // Define specific activity patterns by category
    const activityPatterns: { [key: string]: string[] } = {
      dining: [
        "restaurant",
        "cafe",
        "brunch",
        "dinner",
        "lunch",
        "breakfast",
        "food truck",
        "bistro",
        "pizzeria",
      ],
      outdoor: [
        "hiking",
        "biking",
        "walking",
        "park visit",
        "beach",
        "trail",
        "nature walk",
        "cycling",
      ],
      movies: ["movie", "film", "cinema", "theater"],
      events: [
        "concert",
        "festival",
        "show",
        "performance",
        "exhibition",
        "fair",
      ],
      sports: [
        "basketball",
        "tennis",
        "football",
        "soccer",
        "swimming",
        "gym",
        "fitness",
      ],
      culture: [
        "museum",
        "gallery",
        "historic site",
        "monument",
        "art",
        "cultural center",
      ],
      nightlife: ["bar", "club", "pub", "lounge", "drinks", "cocktails"],
      wellness: ["spa", "massage", "yoga", "meditation", "wellness center"],
    };

    if (category && activityPatterns[category]) {
      for (const pattern of activityPatterns[category]) {
        if (lowerDesc.includes(pattern)) {
          return pattern.charAt(0).toUpperCase() + pattern.slice(1);
        }
      }
    }

    return null;
  };

  // Check if the title is actually a conversational AI response that shouldn't be a title
  const isConversationalResponse = (title: string): boolean => {
    const conversationalPhrases = [
      "great!",
      "sounds like",
      "perfect!",
      "excellent!",
      "that's a",
      "how about",
      "let me",
      "i think",
      "you could",
      "you might",
      "consider",
      "i'd recommend",
      "i suggest",
      "based on",
      "here's a",
      "here are",
      "this could be",
      "this would be",
      "that would be",
      "i'll help",
      "let's plan",
      "we could",
      "you can",
      "it looks like",
      "it seems",
      "wonderful!",
      "fantastic!",
      "amazing!",
      "i love",
      "that sounds",
    ];

    const lowerTitle = title.toLowerCase();

    // Check for conversational phrases
    const hasConversationalPhrase = conversationalPhrases.some((phrase) =>
      lowerTitle.includes(phrase.toLowerCase()),
    );

    // Check for question marks or exclamation patterns
    const hasQuestionOrExclamation =
      title.includes("?") || title.match(/!{2,}/);

    // Check if it's too long to be a proper title (likely a sentence)
    const isTooLong = title.length > 80;

    // Check if it contains complete sentences (has multiple sentence indicators)
    const sentenceCount = (title.match(/[.!?]+/g) || []).length;
    const hasMultipleSentences = sentenceCount > 1;

    return (
      hasConversationalPhrase ||
      !!hasQuestionOrExclamation ||
      isTooLong ||
      hasMultipleSentences
    );
  };

  // Function to filter and clean AI responses to prevent hallucinations
  const filterAIResponse = (response: ParsedAIResponse): ParsedAIResponse => {
    const filteredResponse = { ...response };

    // Clean followUpQuestions
    if (filteredResponse.followUpQuestions) {
      filteredResponse.followUpQuestions = filteredResponse.followUpQuestions
        .map((question) => {
          return cleanText(question);
        })
        .filter(
          (question) =>
            question.length > 0 &&
            question.length <= 200 && // Reasonable length limit
            !isIrrelevantContent(question),
        );
    }

    // Clean suggestedReplies
    if (filteredResponse.suggestedReplies) {
      filteredResponse.suggestedReplies = filteredResponse.suggestedReplies
        .map((reply) => {
          return cleanText(reply);
        })
        .filter(
          (reply) =>
            reply.length > 0 &&
            reply.length <= 50 && // Keep replies short
            !isIrrelevantContent(reply),
        );
    }

    // Clean description if present
    if (filteredResponse.description) {
      // Debug: Check if description contains image URLs
      if (
        filteredResponse.description.includes("http") ||
        filteredResponse.description.includes("unsplash") ||
        filteredResponse.description.includes("images.")
      ) {
        console.warn(
          "⚠️ Image URL detected in description field, cleaning it out:",
          filteredResponse.description,
        );

        // Remove URLs from description
        filteredResponse.description = filteredResponse.description
          .replace(/https?:\/\/[^\s]+/gi, "") // Remove HTTP/HTTPS URLs
          .replace(/www\.[^\s]+/gi, "") // Remove www. URLs
          .replace(
            /[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|io|co|uk|sg)[^\s]*/gi,
            "",
          ) // Remove domain names
          .replace(/\s+/g, " ") // Clean up extra spaces
          .trim();
      }

      filteredResponse.description = cleanText(filteredResponse.description);
      if (
        isIrrelevantContent(filteredResponse.description) ||
        filteredResponse.description.length > 500
      ) {
        // If description is too long or irrelevant, truncate it
        filteredResponse.description =
          filteredResponse.description.substring(0, 497) + "...";
      }
    }

    return filteredResponse;
  };

  // Helper function to clean text content
  const cleanText = (text: string): string => {
    if (!text) return "";

    // Limit emoji usage to maximum 2 per text
    const emojiMatches = text.match(/\p{Emoji}/gu);
    if (emojiMatches && emojiMatches.length > 2) {
      // Keep only the first 2 emojis
      let emojiCount = 0;
      text = text.replace(/\p{Emoji}/gu, (emoji) => {
        emojiCount++;
        return emojiCount <= 2 ? emoji : "";
      });
    }

    // Remove excessive punctuation
    text = text.replace(/[!]{3,}/g, "!!"); // Max 2 exclamation marks
    text = text.replace(/[?]{3,}/g, "??"); // Max 2 question marks
    text = text.replace(/[.]{4,}/g, "..."); // Max 3 dots

    // Clean up extra whitespace
    text = text.replace(/\s+/g, " ").trim();

    return text;
  };

  // Function to capture unmapped AI data into description field
  const captureUnmappedDataToDescription = (
    response: ParsedAIResponse,
  ): ParsedAIResponse => {
    // Define ONLY the core activity fields that should be preserved as separate fields
    const coreFields = new Set([
      "title",
      "location",
      "coordinates",
      "placeId",
      "duration",
      "groupSize",
      "category",
      "suggestedDateTime",
      "description",
      "imageUrl",
      "followUpQuestions",
      "suggestedReplies",
      "timestamp",
      "role",
      "activityDetails",
      "activity",
      "activityForm",
      "form",
      "currentActivity",
      "hasConfirmedDefaults",
    ]);

    const unmappedData: string[] = [];

    // Check ALL properties - anything not in coreFields goes to description as free text
    Object.entries(response).forEach(([key, value]) => {
      if (
        !coreFields.has(key) &&
        value !== undefined &&
        value !== null &&
        value !== ""
      ) {
        // Convert value to a descriptive string
        if (typeof value === "string" && value.length > 0) {
          unmappedData.push(`${key}: ${value}`);
        } else if (typeof value === "number") {
          unmappedData.push(`${key}: ${value}`);
        } else if (typeof value === "boolean") {
          unmappedData.push(`${key}: ${value ? "yes" : "no"}`);
        } else if (Array.isArray(value) && value.length > 0) {
          const arrayStr = value.join(", ");
          if (arrayStr.length > 0 && arrayStr.length < 200) {
            unmappedData.push(`${key}: ${arrayStr}`);
          }
        } else if (typeof value === "object") {
          try {
            const objStr = JSON.stringify(value);
            if (objStr.length > 0 && objStr.length < 200) {
              unmappedData.push(`${key}: ${objStr}`);
            }
          } catch (e) {
            // Skip if can't stringify
          }
        }
      }
    });

    // Also check for rich content in known text fields that might contain additional info
    const additionalInfo: string[] = [];

    // Check if title contains extra information beyond the activity name
    if (response.title && response.title.length > 60) {
      const extraInfo = response.title.substring(60).trim();
      if (extraInfo && !extraInfo.match(/^[.!?]*$/)) {
        additionalInfo.push(extraInfo);
      }
    }

    // Combine all unmapped data
    const allUnmappedInfo = [...unmappedData, ...additionalInfo];

    if (allUnmappedInfo.length > 0) {
      const existingDescription = response.description || "";
      const unmappedText = allUnmappedInfo.join(". ");

      // Combine with existing description, avoiding duplication
      let newDescription = existingDescription;
      if (unmappedText && !existingDescription.includes(unmappedText)) {
        newDescription = existingDescription
          ? `${existingDescription} ${unmappedText}.`
          : unmappedText;
      }

      // Create clean response with only core fields
      const cleanResponse: ParsedAIResponse = {
        title: response.title,
        description: newDescription,
        location: response.location,
        placeId: response.placeId,
        coordinates: response.coordinates,
        suggestedDateTime: response.suggestedDateTime,
        duration: response.duration,
        groupSize: response.groupSize,
        category: response.category,
        followUpQuestions: response.followUpQuestions,
        suggestedReplies: response.suggestedReplies,
        hasConfirmedDefaults: response.hasConfirmedDefaults,
      };

      return cleanResponse;
    }

    return response;
  };

  // Helper function to detect irrelevant content
  const isIrrelevantContent = (text: string): boolean => {
    const lowerText = text.toLowerCase();

    // Flag content that seems like AI hallucination or irrelevant rambling
    const irrelevantPatterns = [
      "as an ai",
      "i am an ai",
      "i'm an ai",
      "being an ai",
      "i don't have personal",
      "i cannot",
      "i can't actually",
      "disclaimer:",
      "please note that",
      "it's important to note",
      "remember that",
      "keep in mind that",
      "it should be noted",
      "according to my knowledge",
      "based on my training",
      "from my understanding",
      "in my database",
      "hypothetically",
      "theoretically",
      "in theory",
      "this is just a suggestion",
      "this is only a recommendation",
    ];

    // Flag nonsensical suggestions that don't make logical sense
    const nonsensicalPatterns = [
      // Time references used inappropriately
      /\d{1,2}(am|pm)\s+for\s+people/i, // "4pm for people"
      /\d{1,2}:\d{2}\s+for\s+people/i, // "4:00 for people"
      /time\s+for\s+location/i, // "time for location"
      /location\s+for\s+time/i, // "location for time"

      // Duration misused as other fields
      /\d+\s+(hours?|minutes?|mins?)\s+for\s+location/i, // "2 hours for location"
      /\d+\s+(hours?|minutes?|mins?)\s+people/i, // "30 minutes people"

      // Group size misused
      /\d+\s+people\s+for\s+(location|time|duration)/i, // "5 people for location"

      // Obvious field confusion
      /singapore\s+for\s+\d+\s+people/i, // "Singapore for 2 people"
      /\d{1,2}(am|pm)\s+location/i, // "3pm location"
      /people\s+at\s+\d{1,2}(am|pm)/i, // "people at 4pm"

      // Incomplete or broken suggestions
      /^(at|for|with|from|to)\s+$/i, // Just prepositions
      /^\d+$/, // Just numbers
      /^(am|pm)$/i, // Just am/pm

      // Repetitive or circular content
      /(.{10,})\1{2,}/i, // Same phrase repeated 3+ times
    ];

    // Check for any irrelevant patterns
    const hasIrrelevantPattern = irrelevantPatterns.some((pattern) =>
      lowerText.includes(pattern),
    );

    // Check for nonsensical patterns
    const hasNonsensicalPattern = nonsensicalPatterns.some((pattern) =>
      pattern.test(text),
    );

    // Flag very short responses that don't provide value
    const isTooShort = text.trim().length < 3;

    // Flag responses that are mostly punctuation or symbols
    const symbolsCount = (text.match(/[^\w\s]/g) || []).length;
    const isMostlySymbols = symbolsCount > text.length * 0.7;

    return (
      hasIrrelevantPattern ||
      hasNonsensicalPattern ||
      isTooShort ||
      isMostlySymbols
    );
  };

  // Helper function to detect generic titles that should be refined
  const isGenericTitle = (title: string): boolean => {
    const genericTitles = [
      "activity plan",
      "activity",
      "event",
      "plan",
      "activity suggestion",
      "suggestion",
      "activity idea",
      "idea",
    ];

    return genericTitles.some((generic) =>
      title.toLowerCase().includes(generic.toLowerCase()),
    );
  };

  const handleSendMessage = useCallback(
    async (messageText?: string, displayText?: string) => {
      // If there's an ongoing API call, abort it
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      const text = (messageText || userInput).trim();
      if (!text || isLoading) {
        return;
      }

      // Prevent rapid successive messages (minimum 500ms between messages)
      const now = Date.now();
      if (now - lastMessageTime < 500) return;
      setLastMessageTime(now);

      // Prevent duplicate messages
      const lastUserMessage = conversation[conversation.length - 1];
      if (
        lastUserMessage &&
        lastUserMessage.role === "user" &&
        lastUserMessage.content === text
      ) {
        return;
      }

      // Clear input immediately to maintain focus
      if (!messageText) {
        setUserInput("");
      }

      if (text.toLowerCase() === "start over") {
        // Check if there's existing form data to warn about
        const lastAiMessage = conversation
          .filter(
            (msg) => msg.role === "model" && isParsedAIResponse(msg.content),
          )
          .pop();

        const hasExistingData =
          lastAiMessage &&
          isParsedAIResponse(lastAiMessage.content) &&
          (lastAiMessage.content.title ||
            lastAiMessage.content.location ||
            lastAiMessage.content.description);

        if (hasExistingData) {
          const safeLastAiContent = isParsedAIResponse(lastAiMessage?.content)
            ? lastAiMessage.content
            : {};
          // Warn user about losing progress
          const warningMessage: AIMessage = {
            role: "model",
            content: {
              ...safeLastAiContent,
              followUpQuestions: [
                "Are you sure you want to start over? This will clear all your current progress.",
              ],
              suggestedReplies: [
                "Yes, start fresh",
                "No, keep my progress",
                "Continue editing",
              ],
            },
            timestamp: generateUniqueTimestamp(),
          };
          setConversation((prev) => [
            ...prev,
            {
              role: "user",
              content: text,
              timestamp: generateUniqueTimestamp(),
            },
            warningMessage,
          ]);
          if (isParsedAIResponse(warningMessage.content)) {
            setSuggestedReplies(warningMessage.content.suggestedReplies || []);
          }
          return;
        }

        // No existing data, safe to start over
        const initialMessage: AIMessage = {
          role: "model",
          content: {
            followUpQuestions: [
              "Fresh start! Let's create something amazing together. What's the vibe you're going for?",
            ],
            suggestedReplies: [
              "Chill & relaxed 😌",
              "Active & energetic �",
              "Social & fun 🎉",
              "Creative & unique 🎨",
            ],
          },
          timestamp: generateUniqueTimestamp(),
        };
        setConversation([initialMessage]);
        if (isParsedAIResponse(initialMessage.content)) {
          setSuggestedReplies(initialMessage.content.suggestedReplies || []);
        }
        return;
      }

      // Handle confirmation for starting over with existing data
      if (text.toLowerCase() === "yes, start fresh") {
        const initialMessage: AIMessage = {
          role: "model",
          content: {
            followUpQuestions: [
              "Fresh start! Let's create something amazing together. What's the vibe you're going for?",
            ],
            suggestedReplies: [
              "Chill & relaxed 😌",
              "Active & energetic 🏃",
              "Social & fun 🎉",
              "Creative & unique 🎨",
            ],
          },
          timestamp: generateUniqueTimestamp(),
        };
        setConversation([initialMessage]);
        if (isParsedAIResponse(initialMessage.content)) {
          setSuggestedReplies(initialMessage.content.suggestedReplies || []);
        }
        return;
      }

      if (text.toLowerCase() === "create manually") {
        handleNavigateToManual();
        return;
      }

      // Handle confirmation responses for posting with defaults
      if (text.includes("Post it!") || text.toLowerCase().includes("post it")) {
        // User confirmed they want to post with current values
        const lastModelMessage = conversation[conversation.length - 1];
        if (
          lastModelMessage &&
          lastModelMessage.role === "model" &&
          isParsedAIResponse(lastModelMessage.content)
        ) {
          const activityData = lastModelMessage.content;

          // Mark as confirmed and proceed to post
          const confirmedData: ParsedAIResponse = {
            ...activityData,
            hasConfirmedDefaults: true,
          };

          // Trigger the posting flow directly
          if (
            confirmedData.title &&
            confirmedData.location &&
            confirmedData.suggestedDateTime &&
            confirmedData.duration &&
            confirmedData.groupSize &&
            confirmedData.category &&
            confirmedData.coordinates
          ) {
            try {
              const currentUserId = currentUser?.id;
              if (!currentUserId) {
                throw new Error("User not authenticated");
              }
              // All required data present, starting activity creation
              setIsLoading(true);
              setConversation((prev) => [
                ...prev,
                {
                  role: "user",
                  content: text,
                  timestamp: generateUniqueTimestamp(),
                },
              ]);

              const newActivityId = await createActivity({
                title: confirmedData.title!,
                description: confirmedData.description || null,
                location: confirmedData.location!,
                coordinates: confirmedData.coordinates,
                dateTime: confirmedData.suggestedDateTime!,
                duration:
                  typeof confirmedData.duration === "string"
                    ? getDurationAsMinutes(confirmedData.duration)
                    : confirmedData.duration!,
                groupSize: confirmedData.groupSize!,
                category: getValidCategory(confirmedData.category),
                creatorId: currentUserId,
                isPrivate: confirmedData.isPrivate || false,
              });

              setConversation((prev) => [
                ...prev,
                {
                  role: "model",
                  content:
                    "🎉 Perfect! Your activity is now live on the feed. Hope you have an amazing time!",
                  timestamp: generateUniqueTimestamp(),
                },
              ]);

              if (newActivityId) {
                allowNavigationRef.current = true;
                navigate(`/activity/${newActivityId}`, { replace: true });

                void (async () => {
                  try {
                    const invitees = proposalInvitees.filter(
                      (id) => id !== currentUserId,
                    );
                    const backgroundTasks: Promise<any>[] = [refreshActivities()];

                    if (invitees.length > 0) {
                      backgroundTasks.push(
                        inviteUsersToActivity(newActivityId, invitees),
                      );
                    }

                    if (proposalId && db) {
                      backgroundTasks.push(
                        updateDoc(doc(db, "matchProposals", proposalId), {
                          status: "created",
                          createdActivityId: newActivityId,
                          createdAt: serverTimestamp(),
                        }),
                      );
                    }

                    await Promise.all(backgroundTasks);
                  } catch (backgroundError) {
                    crashlytics.logError(
                      `Error in AI post-create background sync: ${String(backgroundError)}`,
                      "CreatePage.tsx",
                      backgroundError,
                    );
                  }
                })();
              } else {
                crashlytics.logError(
                  "AI Flow: Activity creation returned null/undefined",
                  "CreatePage.tsx",
                  new Error("Activity creation returned null/undefined"),
                );
                throw new Error("Activity creation failed");
              }
            } catch (error) {
              crashlytics.logError(
                `Error posting activity: ${getErrorMessage(error)}`,
                "CreatePage.tsx",
                error,
              );
              addToast("Failed to post activity. Please try again.", "error");

              // Preserve form data and show error message with context
              const lastAiMessage = conversation
                .filter(
                  (msg) =>
                    msg.role === "model" && isParsedAIResponse(msg.content),
                )
                .pop();

              if (lastAiMessage && isParsedAIResponse(lastAiMessage.content)) {
                const existingData = lastAiMessage.content;
                const hasFormData =
                  existingData.title ||
                  existingData.location ||
                  existingData.description ||
                  existingData.suggestedDateTime;

                const errorMessage: AIMessage = {
                  role: "model",
                  content: {
                    ...existingData,
                    followUpQuestions: [
                      hasFormData
                        ? "There was an issue posting your activity, but all your details are saved! Would you like to try again with your current plan?"
                        : "There was an issue posting your activity. Would you like to try again?",
                    ],
                    suggestedReplies: hasFormData
                      ? [
                          "Try posting again",
                          "Edit details first",
                          "Check location",
                        ]
                      : [
                          "Try posting again",
                          "Edit details",
                          "Create manually",
                        ],
                  },
                  timestamp: generateUniqueTimestamp(),
                };
                setConversation((prev) => [...prev.slice(0, -1), errorMessage]);
                if (isParsedAIResponse(errorMessage.content)) {
                  setSuggestedReplies(
                    errorMessage.content.suggestedReplies || [],
                  );
                }
              } else {
                setConversation((prev) => [...prev.slice(0, -1)]);
              }
            } finally {
              setIsLoading(false);
            }
          }
          return;
        }
      }

      const isRetry = text.toLowerCase() === "try again";
      let conversationForApi: AIMessage[];
      let currentConversationState = conversation;

      if (isRetry) {
        const lastModelMessageIndex = conversation
          .map((m) => m.role)
          .lastIndexOf("model");
        if (lastModelMessageIndex > -1) {
          currentConversationState = conversation.slice(
            0,
            lastModelMessageIndex,
          );
          setConversation(currentConversationState);
        }
        conversationForApi = currentConversationState;
      } else {
        const userMessage: AIMessage = {
          role: "user",
          content: displayText || text,
          timestamp: generateUniqueTimestamp(),
          // @ts-ignore
          apiContent: text,
        };
        currentConversationState = [...conversation, userMessage];
        setConversation(currentConversationState);
        conversationForApi = currentConversationState;

        // Extract activity context from latest AI response
        const lastAiMessage = conversation
          .filter(
            (msg) => msg.role === "model" && isParsedAIResponse(msg.content),
          )
          .pop();

        const activityContext =
          lastAiMessage && isParsedAIResponse(lastAiMessage.content)
            ? {
                title: lastAiMessage.content.title,
                category: lastAiMessage.content.category,
                location: lastAiMessage.content.location,
                description: lastAiMessage.content.description,
              }
            : {};

        // Track AI chat messages for profile insights - reveals user's interests, communication style, what they're looking for
        queueUserAction(
          "ai_chat_message",
          undefined,
          activityContext.title,
          activityContext.category,
          activityContext.location,
          {
            message: text, // Direct expression of preferences and concerns
            activityContext: activityContext, // What activity they're discussing
          },
        );
      }

      setSuggestedReplies([]);
      setIsLoading(true);

      const aiTypingMessage: AIMessage = {
        role: "model",
        content: "",
        timestamp: generateUniqueTimestamp(),
        isTyping: true,
      };
      setConversation((prev) => [...prev, aiTypingMessage]);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        // Abort any ongoing API call when timeout occurs
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }

        if (retryCount < 3) {
          // Automatically retry
          setRetryCount((prev) => prev + 1);
          handleSendMessage("Try Again");
        } else {
          setConversation((prev) => {
            const lastMessage = prev[prev.length - 1];
            if (lastMessage && lastMessage.isTyping) {
              // Preserve any existing form data from conversation
              const lastAiMessage = [...conversationForApi]
                .filter(
                  (msg) =>
                    msg.role === "model" && isParsedAIResponse(msg.content),
                )
                .pop();

              const preservedData =
                lastAiMessage && isParsedAIResponse(lastAiMessage.content)
                  ? lastAiMessage.content
                  : {};

              const hasExistingProgress =
                preservedData.title ||
                preservedData.location ||
                preservedData.description ||
                preservedData.suggestedDateTime;

              const timeoutMessage: AIMessage = {
                role: "model",
                content: {
                  ...preservedData,
                  followUpQuestions: [
                    hasExistingProgress
                      ? "That's taking longer than expected, but your activity progress is saved! Let's try continuing with what we have."
                      : "That's taking longer than expected! Let's try something specific.",
                  ],
                  suggestedReplies: hasExistingProgress
                    ? [
                        "Continue with current",
                        "Try again",
                        "Complete details",
                        "Edit current plan",
                      ]
                    : [
                        "Try again",
                        "Coffee meetup ☕",
                        "Movie night 🍿",
                        "Outdoor walk 🌳",
                      ],
                },
                timestamp: generateUniqueTimestamp(),
                isTyping: false,
              };
              if (isParsedAIResponse(timeoutMessage.content)) {
                setSuggestedReplies(
                  timeoutMessage.content.suggestedReplies || [],
                );
              }
              setIsLoading(false);
              setRetryCount(0); // Reset retry count
              return [...prev.slice(0, -1), timeoutMessage];
            }
            return prev;
          });
        }
      }, 20000); // Increased from 15 to 20 seconds to match streaming improvements

      try {
        // Simplified streaming approach
        let fullResponseText = "";
        abortControllerRef.current = new AbortController();

        try {
          for await (const chunk of generateActivitySuggestionStreamFromCallable(
            conversationForApi,
            abortControllerRef.current.signal,
          )) {
            // Enhanced filtering: Skip empty, non-string, or meaningless chunks
            if (!chunk || typeof chunk !== "string" || !chunk.trim()) {
              continue;
            }

            fullResponseText += chunk;

            // Try to parse and update UI with partial responses (for real-time updates)
            try {
              const jsonString = fullResponseText
                .trim()
                .replace(/^```json\s*|```\s*$/g, "");
              const firstBraceIndex = jsonString.indexOf("{");
              const lastBraceIndex = jsonString.lastIndexOf("}");

              if (firstBraceIndex !== -1 && lastBraceIndex !== -1) {
                const cleanJsonString = jsonString.substring(
                  firstBraceIndex,
                  lastBraceIndex + 1,
                );
                const parsedResponse = JSON.parse(
                  cleanJsonString,
                ) as ParsedAIResponse;

                if (
                  parsedResponse &&
                  typeof parsedResponse === "object" &&
                  parsedResponse.followUpQuestions
                ) {
                  // Update conversation with streaming response first
                  setConversation((prev) => {
                    const newConv = [...prev];
                    const lastMsg = newConv[newConv.length - 1];
                    if (lastMsg?.isTyping) {
                      // Preserve isPrivate from previous content if it exists
                      const prevContent = lastMsg.content as ParsedAIResponse;
                      const updatedContent = {
                        ...parsedResponse,
                        isPrivate:
                          prevContent?.isPrivate !== undefined
                            ? prevContent.isPrivate
                            : parsedResponse.isPrivate,
                      };
                      newConv[newConv.length - 1] = {
                        ...lastMsg,
                        content: updatedContent,
                        isTyping: true,
                      };
                    }
                    return newConv;
                  });
                }
              }
            } catch (parseError) {
              // Continue streaming if partial JSON isn't ready yet
            }
          }
        } catch (streamError) {
          crashlytics.logError(
            `Error during AI response streaming: ${getErrorMessage(streamError)}`,
            "CreatePage.tsx",
            streamError,
          );

          // Preserve any existing form data from conversation
          const lastAiMessage = [...conversationForApi]
            .filter(
              (msg) => msg.role === "model" && isParsedAIResponse(msg.content),
            )
            .pop();

          const preservedData =
            lastAiMessage && isParsedAIResponse(lastAiMessage.content)
              ? lastAiMessage.content
              : {};

          const hasExistingProgress =
            preservedData.title ||
            preservedData.location ||
            preservedData.description ||
            preservedData.suggestedDateTime;

          // Handle specific Google API errors
          if (streamError instanceof Error) {
            // Handle API overload (503 UNAVAILABLE)
            if (
              streamError.message.includes("UNAVAILABLE") ||
              streamError.message.includes("overloaded") ||
              streamError.message.includes("503")
            ) {
              addToast(
                "AI is experiencing high demand. Trying a simple approach!",
                "info",
              );
              fullResponseText = JSON.stringify({
                ...preservedData,
                followUpQuestions: [
                  hasExistingProgress
                    ? "The AI is experiencing high demand right now, but your activity progress is saved. Let's work with what we have!"
                    : "The AI is experiencing high demand right now. Let's plan something simple!",
                ],
                suggestedReplies: hasExistingProgress
                  ? [
                      "Continue current plan",
                      "Try again",
                      "Complete details",
                      "Edit plan",
                    ]
                  : [
                      "Try again",
                      "Movies 🎬",
                      "Coffee & chat ☕",
                      "Quick meal 🍽️",
                    ],
              });
            }
            // Handle cancellation
            else if (streamError.message.includes("cancelled")) {
              fullResponseText = JSON.stringify({
                ...preservedData,
                followUpQuestions: [
                  hasExistingProgress
                    ? "Let's continue with your activity plan! What would you like to work on?"
                    : "Let's try something specific! What sounds good?",
                ],
                suggestedReplies: hasExistingProgress
                  ? [
                      "Continue plan",
                      "Try again",
                      "Complete details",
                      "Edit current",
                    ]
                  : [
                      "Try again",
                      "Movies 🎬",
                      "Coffee & chat ☕",
                      "Outdoor activity 🌳",
                    ],
              });
            }
            // Handle rate limiting
            else if (
              streamError.message.includes("rate") ||
              streamError.message.includes("quota") ||
              streamError.message.includes("429")
            ) {
              addToast("API usage is high. Let's keep it simple!", "info");
              fullResponseText = JSON.stringify({
                ...preservedData,
                followUpQuestions: [
                  hasExistingProgress
                    ? "We're being rate-limited, but your activity progress is saved. Let's keep working on your plan!"
                    : "We're being rate-limited. Let's keep it simple for now!",
                ],
                suggestedReplies: hasExistingProgress
                  ? [
                      "Continue plan",
                      "Try again",
                      "Finish current",
                      "Edit details",
                    ]
                  : [
                      "Try again",
                      "Movies 🎬",
                      "Quick coffee ☕",
                      "Simple meal 🍽️",
                    ],
              });
            } else {
              throw streamError; // Re-throw other errors
            }
          } else {
            throw streamError; // Re-throw non-Error objects
          }
        } finally {
          // Clear timeout and abort controller after streaming (success or failure)
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          // Clean up abort controller
          if (abortControllerRef.current) {
            abortControllerRef.current = null;
          }
        }

        // More robust JSON parsing with fallback
        let parsedResponse: ParsedAIResponse;

        try {
          let jsonString = fullResponseText
            .trim()
            .replace(/^```json\s*|```\s*$/g, "");

          // Handle multiple concatenated JSON objects
          let parsedObjects = [];
          let currentIndex = 0;

          while (currentIndex < jsonString.length) {
            const startIndex = jsonString.indexOf("{", currentIndex);
            if (startIndex === -1) break;

            let braceCount = 0;
            let endIndex = startIndex;

            for (let i = startIndex; i < jsonString.length; i++) {
              if (jsonString[i] === "{") braceCount++;
              else if (jsonString[i] === "}") braceCount--;

              if (braceCount === 0) {
                endIndex = i;
                break;
              }
            }

            if (braceCount === 0) {
              const singleJsonString = jsonString.substring(
                startIndex,
                endIndex + 1,
              );
              try {
                const parsed = JSON.parse(singleJsonString);
                parsedObjects.push(parsed);
              } catch (e) {
                console.warn(
                  "🔍 FAILED TO PARSE JSON OBJECT:",
                  singleJsonString,
                  e,
                );
              }
            }

            currentIndex = endIndex + 1;
          }

          if (parsedObjects.length === 0) {
            // Fallback to original logic
            const firstBraceIndex = jsonString.indexOf("{");
            const lastBraceIndex = jsonString.lastIndexOf("}");

            if (firstBraceIndex === -1 || lastBraceIndex === -1) {
              // Handle plain text responses by converting them to JSON
              const plainText = jsonString.trim();
              if (
                plainText &&
                plainText.length > 0 &&
                !plainText.startsWith("{")
              ) {
                // Generate contextual suggested replies based on the question
                let suggestedReplies = [
                  "East Coast Park 🏖️",
                  "Gardens by the Bay 🌷",
                  "MacRitchie Reservoir 🌳",
                  "Sentosa Island 🏝️",
                ];

                if (
                  plainText.toLowerCase().includes("when") ||
                  plainText.toLowerCase().includes("time")
                ) {
                  suggestedReplies = [
                    "This weekend 🗓️",
                    "Next Saturday ☀️",
                    "Weekday evening �",
                    "Sunday afternoon 🌤️",
                  ];
                } else if (
                  plainText.toLowerCase().includes("how many") ||
                  plainText.toLowerCase().includes("group")
                ) {
                  suggestedReplies = [
                    "2 people 👫",
                    "3-4 people 👥",
                    "5-6 people 👨‍👩‍👧‍👦",
                    "8-10 people 👥👥",
                  ];
                } else if (
                  plainText.toLowerCase().includes("how long") ||
                  plainText.toLowerCase().includes("duration")
                ) {
                  suggestedReplies = [
                    "1 hour ⏰",
                    "2-3 hours ⏱️",
                    "Half day 🕐",
                    "Full day 🌅",
                  ];
                }

                // Convert plain text to JSON structure
                parsedResponse = {
                  followUpQuestions: [plainText],
                  suggestedReplies: suggestedReplies,
                };
              } else {
                throw new Error("No valid JSON structure found");
              }
            }

            const cleanJsonString = jsonString.substring(
              firstBraceIndex,
              lastBraceIndex + 1,
            );
            parsedResponse = JSON.parse(cleanJsonString);
          } else {
            // Merge all parsed objects
            parsedResponse = parsedObjects.reduce(
              (merged, current) => ({
                ...merged,
                ...current,
              }),
              {},
            );
          }

          // Extract data from nested activity object if it exists (try all possible structures)
          const activityData =
            (parsedResponse as any).activity ||
            (parsedResponse as any).activityDetails ||
            (parsedResponse as any).activityForm ||
            (parsedResponse as any).form ||
            (parsedResponse as any).currentActivity ||
            {};

          // Merge activity data into the main response
          if (activityData && Object.keys(activityData).length > 0) {
            parsedResponse = {
              ...parsedResponse,
              ...activityData,
            };
          }

          // Convert string groupSize to number immediately
          if (
            parsedResponse.groupSize &&
            typeof parsedResponse.groupSize === "string"
          ) {
            parsedResponse.groupSize = getGroupSizeAsNumber(
              parsedResponse.groupSize,
            );
          }

          // Convert string duration to minutes immediately
          if (
            parsedResponse.duration &&
            typeof parsedResponse.duration === "string"
          ) {
            parsedResponse.duration = getDurationAsMinutes(
              parsedResponse.duration,
            );
          }

          // Validate basic structure
          if (
            !parsedResponse.followUpQuestions ||
            !Array.isArray(parsedResponse.followUpQuestions)
          ) {
            throw new Error("Invalid response structure");
          }
        } catch (parseError) {
          console.warn(
            `JSON parsing failed - ${fullResponseText} - using fallback response:`,
            parseError,
          );

          // Preserve any existing form data from conversation
          const lastAiMessage = [...conversationForApi]
            .filter(
              (msg) => msg.role === "model" && isParsedAIResponse(msg.content),
            )
            .pop();

          const preservedData =
            lastAiMessage && isParsedAIResponse(lastAiMessage.content)
              ? lastAiMessage.content
              : {};

          const hasExistingProgress =
            preservedData.title ||
            preservedData.location ||
            preservedData.description ||
            preservedData.suggestedDateTime;

          // Provide a helpful fallback instead of erroring
          parsedResponse = {
            ...preservedData,
            followUpQuestions: [
              hasExistingProgress
                ? "I had trouble understanding that response, but your activity progress is saved! What would you like to work on next?"
                : "I had trouble understanding that response. What type of activity sounds good?",
            ],
            suggestedReplies: hasExistingProgress
              ? [
                  "Continue current plan",
                  "Try again",
                  "Complete details",
                  "Edit plan",
                ]
              : [
                  "Try again",
                  "Movies 🎬",
                  "Coffee & chat ☕",
                  "Outdoor activity 🌳",
                ],
          };
        }

        const lastAiMessage = [...conversationForApi]
          .filter(
            (msg) => msg.role === "model" && isParsedAIResponse(msg.content),
          )
          .pop();

        if (
          lastAiMessage &&
          isParsedAIResponse(lastAiMessage.content) &&
          lastAiMessage.content.followUpQuestions?.[0] &&
          parsedResponse.followUpQuestions?.[0] ===
            lastAiMessage.content.followUpQuestions[0]
        ) {
          console.warn("AI loop detected. Intervening.");

          // Preserve existing form data even when breaking loops
          const preservedData = lastAiMessage.content;
          const hasExistingProgress =
            preservedData.title ||
            preservedData.location ||
            preservedData.description ||
            preservedData.suggestedDateTime;

          const loopBreakerContent: ParsedAIResponse = {
            ...(isParsedAIResponse(preservedData) ? preservedData : {}),
            followUpQuestions: [
              hasExistingProgress
                ? "I seem to be repeating myself, but your activity progress is saved! Let's try a different approach to complete your plan."
                : "I seem to be repeating myself. Let's try a different approach.",
            ],
            suggestedReplies: hasExistingProgress
              ? [
                  "Continue current plan",
                  "Try different approach",
                  "Complete details",
                  "Edit plan",
                ]
              : [
                  "Try again",
                  "Something different",
                  "Tell me specifics",
                  "Create Manually",
                ],
          };
          const loopBreakerMessage: AIMessage = {
            role: "model",
            content: loopBreakerContent,
            timestamp: generateUniqueTimestamp(),
          };
          setConversation((prev) => [...prev.slice(0, -1), loopBreakerMessage]);
          setSuggestedReplies(loopBreakerContent.suggestedReplies || []);
          setIsLoading(false);
          return;
        }

        const previousContent =
          (lastAiMessage?.content as ParsedAIResponse) || {};
        // TEMPORARY: Skip merging to test if AI provides data
        let finalParsedResponse: ParsedAIResponse = { ...parsedResponse };

        // Apply title refinement as more information becomes available
        finalParsedResponse = refineActivityTitle(finalParsedResponse);

        // Enhance category detection based on content
        finalParsedResponse = enhanceActivityCategory(finalParsedResponse);

        // Apply content filtering to prevent hallucinations and excessive emojis
        finalParsedResponse = filterAIResponse(finalParsedResponse);

        // Capture unmapped AI data into description field
        finalParsedResponse =
          captureUnmappedDataToDescription(finalParsedResponse);

        const locationToValidate = finalParsedResponse.location;
        const needsValidation =
          locationToValidate &&
          (locationToValidate !== previousContent.location ||
            !finalParsedResponse.coordinates);

        // Critical: If we have a location but no coordinates, we must validate
        const hasLocationWithoutCoordinates =
          finalParsedResponse.location && !finalParsedResponse.coordinates;

        // Skip validation if we already have coordinates and placeId - the location is already validated
        if (needsValidation && !finalParsedResponse.coordinates) {
          try {
            // First try autocomplete for better suggestions
            const suggestions = await getPlaceAutocomplete(locationToValidate);
            if (suggestions && suggestions.length > 0) {
              const topSuggestion = suggestions[0];
              const placeDetails = await getPlaceDetails(
                topSuggestion.place_id,
              );

              finalParsedResponse = {
                ...finalParsedResponse,
                location:
                  placeDetails.formattedAddress || topSuggestion.description,
                placeId: topSuggestion.place_id,
                coordinates: placeDetails.coordinates,
              };
            } else {
              // Fallback to geocoding if autocomplete fails
              finalParsedResponse =
                await validateAndEnhanceLocation(finalParsedResponse);

              // If geocoding also fails, revert to previous content
              if (
                !finalParsedResponse.placeId &&
                !finalParsedResponse.coordinates
              ) {
                finalParsedResponse.location = previousContent.location;
                finalParsedResponse.coordinates = previousContent.coordinates;
                finalParsedResponse.placeId = previousContent.placeId;

                finalParsedResponse.followUpQuestions = [
                  `I couldn't find "${locationToValidate}" on the map, but your other details look great! Could you try a more specific location in Singapore?`,
                ];
                finalParsedResponse.suggestedReplies = [
                  "Try again",
                  "Be more specific",
                  "Different area",
                  "Edit location",
                ];
              }
            }
          } catch (error) {
            crashlytics.logError(
              `Error validating location: ${getErrorMessage(error)}`,
              "CreatePage.tsx",
              error,
            );
            addToast(
              error instanceof Error
                ? error.message
                : "Sorry, I had trouble with that location.",
              "error",
            );

            finalParsedResponse.location = previousContent.location;
            finalParsedResponse.coordinates = previousContent.coordinates;
            finalParsedResponse.placeId = previousContent.placeId;

            finalParsedResponse.followUpQuestions = [
              `I had trouble verifying "${locationToValidate}", but your activity plan is looking good! Please try a different location or check the spelling.`,
            ];
            finalParsedResponse.suggestedReplies = [
              "Try again",
              "Different location",
              "Check spelling",
              "Edit location",
            ];
          }
        }

        // Final safety check: If we have a location but no coordinates, force user to select proper location
        if (finalParsedResponse.location && !finalParsedResponse.coordinates) {
          finalParsedResponse.followUpQuestions = [
            "I need to verify your location on the map to ensure others can find it easily. Could you help me with the exact address?",
          ];
          finalParsedResponse.suggestedReplies = [
            "Try more specific",
            "Different location",
            "Edit location",
            "Add address",
          ];
        }

        const isFinalResponse =
          (!finalParsedResponse.followUpQuestions ||
            finalParsedResponse.followUpQuestions.length === 0) &&
          finalParsedResponse.title &&
          finalParsedResponse.location &&
          finalParsedResponse.coordinates &&
          finalParsedResponse.suggestedDateTime &&
          finalParsedResponse.duration &&
          finalParsedResponse.groupSize &&
          finalParsedResponse.category;

        if (isFinalResponse) {
          const finalData = finalParsedResponse;

          if (
            finalData.suggestedDateTime &&
            toJsDate(finalData.suggestedDateTime) < new Date()
          ) {
            const errorContent: ParsedAIResponse = {
              ...finalData,
              followUpQuestions: [
                "Your activity plan looks great, but the suggested time is in the past! Let's fix the timing to complete your activity.",
              ],
              suggestedReplies: [
                "Try tomorrow",
                "This evening",
                "Pick new time",
                "Edit timing",
              ],
            };
            setConversation((prev) => [
              ...prev.slice(0, -1),
              {
                role: "model",
                content: errorContent,
                timestamp: generateUniqueTimestamp(),
              },
            ]);
            if (isParsedAIResponse(errorContent)) {
              setSuggestedReplies(errorContent.suggestedReplies || []);
            }
            setIsLoading(false);
            return;
          }

          if (!finalData.coordinates) {
            const errorContent: ParsedAIResponse = {
              ...finalData,
              followUpQuestions: [
                "Your activity details look perfect! I just couldn't verify the location. Could you help me with the address?",
              ],
              suggestedReplies: [
                "Try again",
                "Different location",
                "Edit location",
                "Be more specific",
              ],
            };
            setConversation((prev) => [
              ...prev.slice(0, -1),
              {
                role: "model",
                content: errorContent,
                timestamp: generateUniqueTimestamp(),
              },
            ]);
            setSuggestedReplies(errorContent.suggestedReplies || []);
            setIsLoading(false);
            return;
          }

          // Check if we're using default values and should confirm with user
          const usingDefaults = [];
          if (!previousContent.duration && finalData.duration === 60) {
            usingDefaults.push("1-hour duration");
          }
          if (!previousContent.groupSize && finalData.groupSize === 2) {
            usingDefaults.push("2-person group");
          }

          // ALWAYS show confirmation before posting - never auto-post
          // Check if user explicitly said "Post it" to proceed
          const lastUserMsg =
            ([...currentConversationState]
              .reverse()
              .find((msg) => msg.role === "user")?.content as string) || "";
          const userConfirmedPost =
            lastUserMsg.toLowerCase().includes("post it") ||
            lastUserMsg.toLowerCase().includes("looks good") ||
            lastUserMsg === "🎉 Post it!";

          if (!userConfirmedPost) {
            // Show confirmation prompt instead of auto-posting
            let confirmMessage =
              "Perfect! Your activity is all set and ready to post. 🎉";
            if (usingDefaults.length > 0) {
              confirmMessage = `Perfect! Your activity is ready. I've set the ${usingDefaults.join(" and ")} as defaults.`;
            }

            const confirmationContent: ParsedAIResponse = {
              ...finalData,
              followUpQuestions: [
                confirmMessage + " Would you like to post it now?",
              ],
              suggestedReplies: [
                "🎉 Post it!",
                "Let me review",
                "Make changes",
                "Edit details",
              ],
            };
            setConversation((prev) => [
              ...prev.slice(0, -1),
              {
                role: "model",
                content: confirmationContent,
                timestamp: generateUniqueTimestamp(),
              },
            ]);
            setSuggestedReplies(confirmationContent.suggestedReplies || []);
            setIsLoading(false);
            return;
          }

          // User confirmed - proceed with posting
          const currentUserId = currentUser?.id;
          if (!currentUserId) {
            throw new Error("User not authenticated");
          }
          const newActivityId = await createActivity({
            title: finalData.title!,
            description: finalData.description || null,
            location: finalData.location!,
            coordinates: finalData.coordinates,
            dateTime: finalData.suggestedDateTime!,
            duration:
              typeof finalData.duration === "string"
                ? getDurationAsMinutes(finalData.duration)
                : finalData.duration!,
            groupSize: finalData.groupSize!,
            category: getValidCategory(finalData.category),
            creatorId: currentUserId,
            isPrivate: finalData.isPrivate || false,
          });

          setConversation((prev) => [
            ...prev.slice(0, -1),
            {
              role: "model",
              content:
                "🎉 Perfect! Your activity is now live on the feed. Hope you have an amazing time!",
              timestamp: generateUniqueTimestamp(),
            },
          ]);

          if (newActivityId) {
            allowNavigationRef.current = true;
            navigate(`/activity/${newActivityId}`, { replace: true });

            void refreshActivities().catch((backgroundError) => {
              crashlytics.logError(
                `Error refreshing activities after create: ${getErrorMessage(backgroundError)}`,
                "CreatePage.tsx",
                backgroundError,
              );
            });
          } else {
            throw new Error("Activity creation failed");
          }
        } else {
          // Apply manual edit preservation for AI responses (not manual form edits)
          let processedResponse = { ...finalParsedResponse };

          // Get the current data from the most recent AI message for manual edit preservation
          const currentData =
            conversation.length > 0
              ? (conversation[conversation.length - 1]
                  .content as ParsedAIResponse)
              : finalParsedResponse;

          // Get the last user message to check if they're requesting specific changes
          const lastUserMessage =
            currentConversationState.length > 0
              ? ([...currentConversationState]
                  .reverse()
                  .find((msg) => msg.role === "user")?.content as string) || ""
              : "";

          if (lastUserMessage) {
            const userRequestedChanges =
              isUserRequestingFieldChanges(lastUserMessage);

            // Preserve manually edited fields unless user explicitly requested changes
            // Only preserve if there's actually existing data to preserve
            if (
              globalManuallyEditedFields.has("title") &&
              !userRequestedChanges.title &&
              currentData.title
            ) {
              processedResponse.title = currentData.title;
            }

            if (
              globalManuallyEditedFields.has("description") &&
              !userRequestedChanges.description &&
              currentData.description !== undefined
            ) {
              processedResponse.description = currentData.description;
            }

            if (
              globalManuallyEditedFields.has("location") &&
              !userRequestedChanges.location &&
              currentData.location
            ) {
              processedResponse.location = currentData.location;
              processedResponse.placeId = currentData.placeId;
              processedResponse.coordinates = currentData.coordinates;
            }

            if (
              globalManuallyEditedFields.has("dateTime") &&
              !userRequestedChanges.dateTime &&
              currentData.suggestedDateTime
            ) {
              processedResponse.suggestedDateTime =
                currentData.suggestedDateTime;
            }

            if (
              globalManuallyEditedFields.has("duration") &&
              !userRequestedChanges.duration &&
              currentData.duration
            ) {
              processedResponse.duration = currentData.duration;
            }

            if (
              globalManuallyEditedFields.has("groupSize") &&
              !userRequestedChanges.groupSize &&
              currentData.groupSize
            ) {
              processedResponse.groupSize = currentData.groupSize;
            }

            if (
              globalManuallyEditedFields.has("category") &&
              !userRequestedChanges.category &&
              currentData.category
            ) {
              processedResponse.category = currentData.category;
            }

            // Clear manual edit flags for explicitly requested changes
            clearManualEditForFields(userRequestedChanges);
          }
          // Always preserve isPrivate setting from current data (user-controlled toggle)
          if (currentData.isPrivate !== undefined) {
            processedResponse.isPrivate = currentData.isPrivate;
          }

          const aiResponseMessage: AIMessage = {
            role: "model",
            content: processedResponse,
            timestamp: generateUniqueTimestamp(),
          };
          setConversation((prev) => [...prev.slice(0, -1), aiResponseMessage]);
          setSuggestedReplies(processedResponse.suggestedReplies || []);
        }
      } catch (error) {
        crashlytics.logError(
          `Error during AI response processing: ${getErrorMessage(error)}`,
          "CreatePage.tsx",
          error,
        );

        // Handle aborted requests gracefully (don't show error to user)
        if (
          error instanceof Error &&
          (error.name === "AbortError" ||
            error.message.includes("aborted") ||
            error.message.includes("cancelled"))
        ) {
          // Just clean up the loading state without showing an error
          setConversation((prev) => prev.slice(0, -1)); // Remove the typing indicator
          return;
        }

        // Preserve any existing form data from the conversation
        const lastAiMessage = [...conversation]
          .filter(
            (msg) => msg.role === "model" && isParsedAIResponse(msg.content),
          )
          .pop();

        const preservedData =
          lastAiMessage && isParsedAIResponse(lastAiMessage.content)
            ? lastAiMessage.content
            : {};

        const hasExistingProgress =
          preservedData.title ||
          preservedData.location ||
          preservedData.description ||
          preservedData.suggestedDateTime;

        const errorMessage: AIMessage = {
          role: "model",
          content: {
            // Preserve any existing form data
            ...preservedData,
            followUpQuestions: [
              hasExistingProgress
                ? "Something went wrong, but don't worry - your activity progress is saved! Let's continue with what we have."
                : "Something went wrong, but don't worry! Let's try something specific instead.",
            ],
            suggestedReplies: hasExistingProgress
              ? [
                  "Continue plan",
                  "Try again",
                  "Complete details",
                  "Edit current",
                ]
              : [
                  "Try again",
                  "Movies 🎬",
                  "Coffee & chat ☕",
                  "Outdoor walk 🌳",
                ],
          },
          timestamp: generateUniqueTimestamp(),
        };
        setConversation((prev) => [...prev.slice(0, -1), errorMessage]);
        if (isParsedAIResponse(errorMessage.content)) {
          setSuggestedReplies(errorMessage.content.suggestedReplies || []);
        }
      } finally {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsLoading(false);
        setRetryCount(0); // Reset retry count on success or final error
      }
    },
    [
      userInput,
      isLoading,
      conversation,
      createActivity,
      refreshActivities,
      inviteUsersToActivity,
      proposalInvitees,
      proposalId,
      currentUser,
      navigate,
      addToast,
      handleNavigateToManual,
      retryCount,
    ],
  );

  const contentTopPosition = isNative
    ? `calc(max(env(safe-area-inset-top, 0px), 0px) + ${headerHeight}px`
    : `calc(max(env(safe-area-inset-top, 0px), 0px) + ${headerHeight}px)`;

  // On native platforms, when keyboard is open, don't add navHeight (bottom nav is hidden)
  const contentBottomPosition = isNative
    ? `${footerHeight + (keyboardHeight > 0 ? (isAndroid ? 0 : keyboardHeight) : navHeight)}px`
    : `calc(max(env(safe-area-inset-bottom, 0px), 0px) + ${footerHeight}px + ${navHeight}px)`;

  // Footer position: when keyboard is open, position directly above keyboard (no nav spacing)
  const footerBottomPosition = isNative
    ? `${keyboardHeight > 0 ? (isAndroid ? 0 : keyboardHeight) : navHeight}px`
    : `calc(max(env(safe-area-inset-bottom, 0px), 0px) + ${navHeight}px)`;


  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Responsive container: mobile-first with larger breakpoints */}
      <div className="w-full max-w-md md:max-w-2xl lg:max-w-4xl mx-auto h-full flex flex-col">
        <header
          className="page-header border-b border-gray-200/80 bg-white fixed md:px-6 pb-3 pt-3 flex items-center justify-between shadow-sm z-10 right-0 left-0"
          ref={headerRef}
          style={{
            top: "max(env(safe-area-inset-top, 0px), 0px)",
            position: "fixed", // Explicitly set
            touchAction: "none", // Prevent touch gestures on header
          }}
        >
          <div className="flex items-center pl-3">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 mr-3 p-2 rounded-full hover:bg-gray-100"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-lg md:text-xl font-bold text-gray-900">
              Create with AI
            </h1>
          </div>
          <div className="flex items-center gap-2 pr-3">
            <div className="relative">
              <button
                onClick={() => {
                  setShowSearchModal(true);
                  setShowIdeasHint(false);
                }}
                title="Get ideas"
                className={`text-purple-600 p-2 rounded-full hover:bg-purple-50 transition-colors ${showIdeasHint ? "bg-purple-100 ring-2 ring-purple-400 ring-offset-2" : ""}`}
              >
                <BrainCircuitIcon className="w-5 h-5" />
              </button>
              <AnimatePresence>
                {showIdeasHint && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.9 }}
                    className="absolute top-full right-0 mt-2 w-64 bg-white p-3 rounded-xl shadow-xl border border-purple-100 z-50"
                  >
                    <div className="absolute -top-2 right-3 w-4 h-4 bg-white border-t border-l border-purple-100 transform rotate-45"></div>
                    <div className="relative z-10 flex items-start gap-3">
                      <div className="bg-purple-100 p-1.5 rounded-full flex-shrink-0">
                        <BrainCircuitIcon className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-900">
                          Need ideas?
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Tap here to find activities based on what's happening
                          today! 🇸🇬✨
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowIdeasHint(false);
                        }}
                        className="text-gray-400 hover:text-gray-600 p-1 -mt-1 -mr-1"
                      >
                        <XIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              onClick={handleResetActivity}
              title="Start over"
              className="text-gray-500 p-2 rounded-full hover:bg-gray-100 hover:text-primary transition-colors"
            >
              <RefreshCwIcon className="w-5 h-5" />
            </button>
            <button
              onClick={handleNavigateToManual}
              title="Create manually"
              className="text-gray-500 p-2 rounded-full hover:bg-gray-100 hover:text-primary transition-colors"
            >
              <Edit2Icon className="w-5 h-5" />
            </button>
          </div>
        </header>

        <main
          ref={messagesContainerRef}
          className={`overflow-y-auto bg-chat-pattern space-y-1 px-4 pt-4 pb-4`}
          style={{
            position: "fixed",
            top: contentTopPosition /* Safe area + header height */,
            bottom: contentBottomPosition /* Safe area + footer + nav */,
            left: "0",
            right: "0",
            WebkitOverflowScrolling:
              "touch" /* Enable momentum scrolling on iOS */,
            touchAction: "pan-y" /* Allow vertical scrolling */,
            overscrollBehavior: "none" /* Prevent parent scroll */,
            transition: "top 0.2s ease-out, bottom 0.2s ease-out", // Add smooth transition
          }}
        >
          <AnimatePresence>
            {conversation.map((msg, index) => {
              if (msg.role === "user") {
                return (
                  <UserBubble
                    key={`user-${index}-${msg.timestamp}`}
                    message={msg.content as string}
                  />
                );
              } else {
                let isLastAiMessage = index === conversation.length - 1;
                let bubbleDisabled = !isLastAiMessage || isLoading;
                return (
                  <AIBubble
                    key={`ai-${index}-${msg.timestamp}`}
                    message={msg}
                    onUpdate={handleUpdateAIMessage}
                    isApiLoaded={isApiLoaded}
                    addToast={addToast}
                    onPost={handlePostActivity}
                    addToGlobalManualEdits={addToGlobalManualEdits}
                    onEditingInline={setIsEditingInline}
                    disabled={bubbleDisabled}
                    onCreateFromProposal={
                      proposalDraft ? handleCreateFromProposal : undefined
                    }
                    isCreatingFromProposal={isCreatingFromProposal}
                    proposalInviteeLabel={proposalInviteeLabel}
                    proposalInviteeUsers={proposalInviteeUsers}
                    showManageInviteesModal={showManageInviteesModal}
                    setShowManageInviteesModal={setShowManageInviteesModal}
                    inviteeSearchQuery={inviteeSearchQuery}
                    setInviteeSearchQuery={setInviteeSearchQuery}
                    inviteeSearchResults={inviteeSearchResults}
                    setInviteeSearchResults={setInviteeSearchResults}
                    inviteeSearchIsLoading={inviteeSearchIsLoading}
                    inviteeSearchIsLoadingMore={inviteeSearchIsLoadingMore}
                    inviteeSearchHasMore={inviteeSearchHasMore}
                    loadMoreInviteeSearch={loadMoreInviteeSearch}
                    handleRemoveInvitee={handleRemoveInvitee}
                    handleAddInvitee={handleAddInvitee}
                    handleSearchInvitees={handleSearchInvitees}
                  />
                );
              }
            })}
            ;
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </main>

        {/* Add the new message indicator button in the UI, positioned above the footer */}
        {showNewMessageIndicator && (
          <button
            tabIndex={-1}
            onPointerDown={(e) => {
              // Prevent button from receiving focus, but allow click/touch to propagate
              e.preventDefault();
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              // Don't preventDefault here - it's a passive listener
              scrollButtonTouchStartTime.current = Date.now();
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              // Don't preventDefault here - it's a passive listener

              // Only execute on short tap (< 500ms), ignore long press
              const touchDuration =
                Date.now() - scrollButtonTouchStartTime.current;
              if (touchDuration < 500) {
                handleScrollToBottom();
              }
              scrollButtonTouchStartTime.current = 0;
            }}
            onTouchCancel={(e) => {
              e.stopPropagation();
              // Don't preventDefault here - it's a passive listener
              scrollButtonTouchStartTime.current = 0;
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={handleScrollToBottom}
            className="fixed left-1/2 -translate-x-1/2 z-[60] bg-white text-primary px-4 py-2 rounded-full shadow-xl border border-gray-200 hover:bg-gray-50 transition-all select-none"
            style={{
              top: `calc(${contentTopPosition} + 1rem)`,
              pointerEvents: "auto",
              WebkitUserSelect: "none",
              userSelect: "none",
              WebkitTouchCallout: "none",
            }}
            aria-label="Scroll to bottom"
          >
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </div>
          </button>
        )}

        <footer
          ref={footerRef}
          className="message-input-container fixed left-0 right-0 bg-white p-3 md:p-4 lg:p-6 border-t border-gray-200"
          style={{
            bottom: footerBottomPosition,
            transition: "bottom 0.2s ease-out", // Add smooth transition
          }}
        >
          <AnimatePresence>
            {!(keyboardHeight > 0) &&
              suggestedReplies.length > 0 &&
              !isLoading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex flex-wrap gap-2 mb-3 px-1"
                >
                  {suggestedReplies.map((reply, i) => (
                    <SuggestedReply
                      key={i}
                      text={reply}
                      onClick={() => handleSendMessage(reply)}
                      disabled={isLoading}
                    />
                  ))}
                </motion.div>
              )}
          </AnimatePresence>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-center space-x-3"
          >
            <input
              ref={inputRef}
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder={
                isLoading ? "Who's in? is thinking..." : "Type your message..."
              }
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="sentences"
              className="w-full bg-gray-100 border-gray-200 border rounded-full px-4 md:px-5 py-3 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 placeholder-gray-500 transition-all duration-200 text-sm md:text-base"
              disabled={isLoading}
            />
            {userInput && userInput.trim().length > 0 && (
              <button
                type="submit"
                onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on click
                className="bg-primary text-white rounded-full p-3 md:p-4 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity transform hover:scale-110 shadow-lg shadow-primary/30 flex-shrink-0"
                disabled={isLoading || !userInput.trim()}
              >
                <SendIcon className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            )}
          </form>
        </footer>
      </div>
      {/* End of responsive container */}
      {(blocker.state === "blocked" || resetClicked) && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] px-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white p-8 rounded-[2rem] shadow-2xl max-w-sm w-full text-center border border-gray-100"
          >
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 leading-tight">
              Ditch your progress?
            </h3>
            <p className="text-gray-500 mt-3 text-sm leading-relaxed">
              {manualBlockMessage ||
                "You're in the middle of creating with AI. If you leave now, these recommendations will disappear forever."}
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <button
                onClick={handleCancel}
                className="w-full py-3.5 bg-primary text-white rounded-2xl font-bold shadow-lg shadow-primary/20 active:scale-95 transition-transform"
              >
                Keep Creating
              </button>
              <button
                onClick={handleDiscard}
                className="w-full py-2 text-gray-400 font-medium text-sm hover:text-red-500 transition-colors"
              >
                Discard and Exit
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Search Modal for AI-powered inspiration */}
      {showSearchModal && (
        <Portal>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-end md:items-center justify-center z-[100]"
            onClick={() => setShowSearchModal(false)}
            style={{
              paddingBottom:
                keyboardHeight > 0
                  ? isAndroid
                    ? `0px`
                    : searchModalKeyboardHeight > 0
                      ? `${searchModalKeyboardHeight}px`
                      : "0px"
                  : `calc(max(env(safe-area-inset-bottom, 0px), 0px))`, // Safe area
            }}
          >
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full md:max-w-2xl md:rounded-[2rem] rounded-t-[2rem] shadow-2xl flex flex-col"
              style={{
                maxHeight: isAndroid
                  ? "90vh"
                  : searchModalKeyboardHeight > 0
                    ? `calc(90vh - ${searchModalKeyboardHeight}px)`
                    : "90vh",
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <BrainCircuitIcon className="w-6 h-6 text-purple-600" />
                  <h2 className="text-xl font-bold text-gray-900">Get Ideas</h2>
                </div>
                <button
                  onClick={() => setShowSearchModal(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4">
                {isSearchLoading ? (
                  <div className="flex flex-col items-center justify-center text-center py-12 px-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Finding perfect ideas for you...
                    </h3>
                    <p className="text-sm text-gray-500 max-w-sm">
                      AI is searching the web and analyzing your preferences to
                      suggest personalized activities. This usually takes 10-15
                      seconds.
                    </p>
                  </div>
                ) : searchError ? (
                  <div className="text-center py-12 text-red-500">
                    {searchError}
                  </div>
                ) : (searchResult || (!searchQuery && prefetchedIdeas)) &&
                  (searchResult?.ideas || prefetchedIdeas?.ideas)?.length >
                    0 ? (
                  <div className="space-y-4">
                    {(() => {
                      const displayResult = searchResult || prefetchedIdeas;
                      const isPersonalized = !searchQuery && prefetchedIdeas;
                      return (
                        <>
                          {(() => {
                            const ideas: ActivityIdea[] = displayResult?.ideas || [];
                            return (
                              <p className="text-sm text-gray-500 mb-4">
                                {isPersonalized
                                  ? `${ideas.length} personalized ideas for you. Click any to create a draft activity:`
                                  : `Found ${ideas.length} ideas for you. Click any to create a draft activity:`}
                              </p>
                            );
                          })()}
                          {(displayResult?.ideas || []).map((idea: ActivityIdea, index: number) => (
                            <motion.button
                              key={index}
                              onClick={async () => {
                                // Close modal
                                setShowSearchModal(false);
                                // Reset search state
                                setSearchQuery("");
                                setSearchResult(null);
                                // Send the idea's prompt to the AI chat
                                const displayPrompt = `Creating your activity with the Idea: ${idea.title.length > 100 ? idea.title.substring(0, 100) + "..." : idea.title}\n\nPlease review your activity before posting.`;
                                await handleSendMessage(
                                  idea.prompt,
                                  displayPrompt,
                                );
                              }}
                              className="w-full text-left bg-white border-2 border-gray-200 hover:border-primary rounded-2xl p-4 transition-all hover:shadow-lg group"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                  <span className="text-xl">
                                    {index === 0
                                      ? "🎯"
                                      : index === 1
                                        ? "✨"
                                        : index === 2
                                          ? "🎉"
                                          : index === 3
                                            ? "🌟"
                                            : "💡"}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-primary transition-colors">
                                    {idea.title}
                                  </h3>
                                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                    {idea.description}
                                  </p>
                                  {idea.sources && idea.sources.length > 0 && (
                                    <div className="flex items-center gap-1 text-xs text-gray-400">
                                      <LinkIcon className="w-3 h-3" />
                                      <span>
                                        {idea.sources.length} source
                                        {idea.sources.length > 1 ? "s" : ""}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="flex-shrink-0">
                                  <div className="text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                                    <SendIcon className="w-5 h-5" />
                                  </div>
                                </div>
                              </div>
                            </motion.button>
                          ))}
                          {searchResult && (
                            <button
                              onClick={() => {
                                setSearchQuery("");
                                setSearchResult(null);
                                setSearchError(null);
                              }}
                              className="w-full py-3 text-primary font-medium hover:bg-gray-50 rounded-xl transition-colors text-sm"
                            >
                              Search Again
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-center py-12">
                    <BrainCircuitIcon className="w-20 h-20 text-purple-300 mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Find Your Next Adventure
                    </h3>
                    <p className="text-gray-500 max-w-sm">
                      Ask anything like "fun group activities for this weekend"
                      or "good food places in Singapore"
                    </p>
                  </div>
                )}
              </div>

              {/* Search Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!searchQuery.trim() || isSearchLoading) return;

                    setIsSearchLoading(true);
                    setSearchResult(null);
                    setSearchError(null);

                    // Track search query for profile insights
                    queueUserAction(
                      "search_query",
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      {
                        query: searchQuery,
                        source: "ideas_modal",
                      },
                    );

                    try {
                      // Add current query to history (limit to last 50)
                      const updatedHistory = [
                        ...searchHistory,
                        searchQuery,
                      ].slice(-50);
                      setSearchHistory(updatedHistory);

                      // Save to Firestore for persistence across devices
                      const firestore = db;
                      if (currentUser && firestore) {
                        const userRef = doc(firestore, "users", currentUser.id);
                        await updateDoc(userRef, {
                          searchHistory: updatedHistory,
                        }).catch((err) =>
                          crashlytics.logError(
                            `Error updating search history: ${getErrorMessage(err)}`,
                            "CreatePage.tsx",
                            err,
                          ),
                        );
                      }

                      const response = await generateActivitySearchIdeasCallable({
                        query: searchQuery,
                        user: currentUser || undefined,
                        userActivities: userActivityHistory,
                        searchHistory: updatedHistory,
                      });
                      setSearchResult(response);

                      // Track generated ideas for profile insights - shows what topics/categories user explores
                      if (response?.ideas && response.ideas.length > 0) {
                        queueUserAction(
                          "ai_idea_generate",
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          {
                            query: searchQuery,
                            categories: [
                              ...new Set(
                                response.ideas.map(
                                  (idea: any) => idea.category || "other",
                                ),
                              ),
                            ],
                            activityCount: response.ideas.length,
                            source: "ideas_search",
                          },
                        );
                      }
                    } catch (err) {
                      setSearchError(
                        "An unexpected error occurred. Please try again.",
                      );
                      crashlytics.logError(
                        `Error during search: ${getErrorMessage(err)}`,
                        "CreatePage.tsx",
                        err,
                      );
                    } finally {
                      setIsSearchLoading(false);
                    }
                  }}
                  className="flex items-center gap-2"
                >
                  <div className="relative flex-1">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Ask AI for ideas..."
                      className="w-full bg-gray-100 rounded-full px-4 py-3 pl-10 focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 placeholder-gray-500"
                      disabled={isSearchLoading}
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      <SearchIcon className="w-5 h-5" />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="bg-primary text-white rounded-full p-3 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity transform hover:scale-110 shadow-lg shadow-primary/20"
                    disabled={isSearchLoading || !searchQuery.trim()}
                  >
                    <SendIcon className="w-5 h-5" />
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </Portal>
      )}
    </div>
  );
};

export default CreatePage;
